import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import ConfirmModal from './ConfirmModal';
import UnsavedChangesModal from './UnsavedChangesModal';
import DatePicker from './DatePicker';
import TimePicker from './TimePicker';
import Notification from './Notification';
import { ServerIcon } from '../utils/serverIcons';
import { getFormsUrl } from '../config';
import * as XLSX from 'xlsx';
import './Applications.css';
import './FormModal.css';
import './ManagerManagement.css';

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [presets, setPresets] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [viewingSubmissions, setViewingSubmissions] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    intro_text: '',
    completion_text: '',
    is_open: true,
    questions: [],
    preset_id: null,
    auto_close_at: '',
    allowed_viewers: []
  });
  const [settingsAppId, setSettingsAppId] = useState(null);
  const [settingsData, setSettingsData] = useState({
    auto_close_at: '',
    auto_close_enabled: false,
    restrict_viewers: false,
    allowed_roles: []
  });
  const [siteRoles, setSiteRoles] = useState([]);
  const [isCuratorModerator, setIsCuratorModerator] = useState(false);
  const [userRoles, setUserRoles] = useState([]);

  useEffect(() => {
    fetchApplications();
    fetchPresets();
    fetchServers();
    fetchUserRoles();
  }, []);

  const fetchServers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/servers', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setServers(data);
      }
    } catch (err) {
      console.error('Error fetching servers:', err);
    }
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/applications', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка при загрузке заявок');
      }

      const data = await response.json();
      setApplications(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplication = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/applications/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка при загрузке заявки');
      }

      return await response.json();
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  const fetchPresets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/application-presets', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPresets(data);
      }
    } catch (err) {
      console.error('Error fetching presets:', err);
    }
  };

  const fetchUserRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/check-roles', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const roles = data.roles || [];
        setUserRoles(roles);
        const isCurator = roles.includes('admin');
        setIsCuratorModerator(isCurator);
        
        console.log('User roles:', roles);
        console.log('Is admin:', isCurator);
        
        // Если admin, загружаем список модераторов
        if (isCurator) {
          fetchSiteRoles();
        }
      }
    } catch (err) {
      console.error('Error fetching user roles:', err);
    }
  };

  const fetchSiteRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/applications/site-roles', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSiteRoles(data.roles || []);
      }
    } catch (err) {
      console.error('Error fetching site roles:', err);
    }
  };

  const handleAddClick = () => {
    setEditingId(null);
    setFormData({
      title: '',
      description: '',
      intro_text: '',
      is_open: true,
      questions: [],
      preset_id: null,
      auto_close_at: '',
      allowed_viewers: []
    });
    setShowForm(true);
  };

  const handlePresetSelect = async (presetId) => {
    if (!presetId) {
      setFormData(prev => ({ ...prev, preset_id: null, questions: [] }));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/application-presets/${presetId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const preset = await response.json();
        // Фильтруем вопросы пресета, убирая Discord ID и Никнейм (они добавляются автоматически)
        const filteredQuestions = preset.questions
          .filter(q => q.question_text !== 'Discord ID' && q.question_text !== 'Никнейм')
          .map(q => ({
            id: q.id || `temp-${Date.now()}-${Math.random()}`, // Убеждаемся, что у каждого вопроса есть ID
            question_text: q.question_text,
            question_description: q.question_description || '',
            question_type: q.question_type,
            options: q.options ? (typeof q.options === 'string' ? JSON.parse(q.options) : q.options) : null,
            is_required: q.is_required,
            has_other_option: q.has_other_option || false
          }));
        
        setFormData(prev => ({
          ...prev,
          preset_id: presetId,
          description: preset.description ?? prev.description ?? '',
          intro_text: preset.intro_text || prev.intro_text || '',
          completion_text: preset.completion_text || prev.completion_text || '',
          questions: filteredQuestions
        }));
      }
    } catch (err) {
      setError('Ошибка при загрузке пресета');
    }
  };

  const handleSettingsClick = async (id) => {
    const app = await fetchApplication(id);
    if (app) {
      const allowedViewers = app.allowed_viewers ? (typeof app.allowed_viewers === 'string' ? JSON.parse(app.allowed_viewers) : app.allowed_viewers) : [];
      setSettingsAppId(id);
      const allowedRoles = app.allowed_roles ? (Array.isArray(app.allowed_roles) ? app.allowed_roles : []) : [];
      setSettingsData({
        auto_close_at: app.auto_close_at || null,
        auto_close_enabled: !!(app.auto_close_at),
        restrict_viewers: allowedRoles.length > 0 || (allowedViewers && Array.isArray(allowedViewers) && allowedViewers.length > 0),
        allowed_roles: allowedRoles
      });
      
      if (isCuratorModerator && siteRoles.length === 0) {
        fetchSiteRoles();
      }
    }
  };

  const handleEditClick = async (id) => {
    const app = await fetchApplication(id);
    if (app) {
      setEditingId(id);
      // Фильтруем системные вопросы (Discord ID и Никнейм) - они отображаются автоматически
      const userQuestions = app.questions
        .filter(q => q.question_text !== 'Discord ID' && q.question_text !== 'Никнейм')
        .map(q => ({
          ...q,
          id: q.id || q.question_id || `temp-${Date.now()}-${Math.random()}`, // Убеждаемся, что у каждого вопроса есть ID
          question_description: q.question_description || '',
          options: q.options ? (typeof q.options === 'string' ? JSON.parse(q.options) : q.options) : null,
          has_other_option: q.has_other_option || false
        }));
      
      setFormData({
        title: app.title,
        description: app.description || '',
        intro_text: app.intro_text || '',
        completion_text: app.completion_text || '',
        is_open: app.is_open,
        questions: userQuestions
      });
      setShowForm(true);
    }
  };

  const handlePreviewClick = async (id) => {
    const app = await fetchApplication(id);
    if (app) {
      setPreviewId(id);
    }
  };

  const handleToggleOpen = async (id, currentStatus) => {
    setConfirmModal({
      isOpen: true,
      title: currentStatus ? 'Закрыть приём заявок?' : 'Открыть приём заявок?',
      message: currentStatus 
        ? 'После закрытия пользователи не смогут отправлять новые заявки.'
        : 'После открытия пользователи смогут отправлять новые заявки.',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`/api/applications/${id}/toggle`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Ошибка при изменении статуса');
          }

          setSuccess(`Приём заявок ${currentStatus ? 'закрыт' : 'открыт'}`);
          fetchApplications();
        } catch (err) {
          setError(err.message);
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const handleDelete = async (id) => {
    setConfirmModal({
      isOpen: true,
      title: 'Удалить заявку?',
      message: 'Это действие нельзя отменить. Все вопросы и отправленные заявки будут удалены.',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`/api/applications/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Ошибка при удалении');
          }

          setSuccess('Заявка удалена');
          fetchApplications();
        } catch (err) {
          setError(err.message);
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const handleViewSubmissions = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/applications/${id}/submissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка при загрузке отправленных заявок');
      }

      const data = await response.json();
      const app = applications.find(a => a.id === id);
      setViewingSubmissions({ id, submissions: data, title: app?.title || 'Заявки' });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleQuestionChange = (index, field, value) => {
    // Проверяем, не пытаемся ли изменить текст вопроса Discord ID или Никнейм
    const question = formData.questions[index];
    if (question && (question.question_text === 'Discord ID' || question.question_text === 'Никнейм')) {
      if (field === 'question_text') {
        setError('Нельзя изменить текст системных полей Discord ID и Никнейм');
        return;
      }
    }
    
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[index] = {
        ...newQuestions[index],
        [field]: value
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleAddQuestion = (afterIndex = null) => {
    setFormData(prev => {
      // Генерируем уникальный ID на основе текущего количества вопросов и времени
      // Это гарантирует, что новый вопрос будет иметь уникальный ID, который не будет конфликтовать с существующими
      let questionIdCounter = Date.now();
      if (prev.questions.length > 0) {
        const ids = prev.questions.map(q => {
          if (q.id && typeof q.id === 'string') {
            const match = q.id.match(/temp-(\d+)/);
            return match ? parseInt(match[1]) : 0;
          }
          return 0;
        });
        if (ids.length > 0) {
          questionIdCounter = Math.max(...ids) + 1;
        }
      }
      
      const newQuestion = {
        id: `temp-${questionIdCounter}-${Math.random().toString(36).substr(2, 9)}`, // Временный уникальный ID
        question_text: '',
        question_description: '',
        question_type: 'text',
        options: null,
        is_required: true,
        has_other_option: false
      };
      
      if (afterIndex === null || afterIndex === undefined) {
        // Добавляем в конец массива пользовательских вопросов
        const updatedQuestions = [...prev.questions, newQuestion];
        return {
          ...prev,
          questions: updatedQuestions
        };
      } else {
        // Добавляем после указанного индекса в массиве пользовательских вопросов
        const newQuestions = [...prev.questions];
        newQuestions.splice(afterIndex + 1, 0, newQuestion);
        return {
          ...prev,
          questions: newQuestions
        };
      }
    });
  };

  const handleRemoveQuestion = (index) => {
    // Проверяем, не является ли вопрос Discord ID или Никнейм - их нельзя удалять
    const question = formData.questions[index];
    if (question && (question.question_text === 'Discord ID' || question.question_text === 'Никнейм')) {
      setError('Нельзя удалить системные поля Discord ID и Никнейм');
      return;
    }
    
    const questionText = question?.question_text || 'вопрос';
    
    setConfirmModal({
      isOpen: true,
      title: 'Удалить вопрос?',
      message: `Вы уверены, что хотите удалить вопрос "${questionText}"? Это действие нельзя отменить.`,
      onConfirm: () => {
        setFormData(prev => ({
          ...prev,
          questions: prev.questions.filter((_, i) => i !== index)
        }));
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  const handleMoveQuestion = (index, direction) => {
    // Проверяем, не является ли вопрос Discord ID или Никнейм - их нельзя перемещать
    const question = formData.questions[index];
    if (question && (question.question_text === 'Discord ID' || question.question_text === 'Никнейм')) {
      setError('Нельзя переместить системные поля Discord ID и Никнейм');
      return;
    }
    
    // Проверяем, не пытаемся ли переместить вопрос на место Discord ID или Никнейм
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const targetQuestion = formData.questions[newIndex];
    if (targetQuestion && (targetQuestion.question_text === 'Discord ID' || targetQuestion.question_text === 'Никнейм')) {
      setError('Нельзя переместить вопрос на место системных полей Discord ID и Никнейм');
      return;
    }
    
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === formData.questions.length - 1)
    ) {
      return;
    }

    setFormData(prev => {
      const newQuestions = [...prev.questions];
      [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];
      return { ...prev, questions: newQuestions };
    });
  };

  const handleQuestionTypeChange = (index, newType) => {
    // Проверяем, не пытаемся ли изменить тип вопроса Discord ID или Никнейм
    const question = formData.questions[index];
    if (question && (question.question_text === 'Discord ID' || question.question_text === 'Никнейм')) {
      setError('Нельзя изменить тип системных полей Discord ID и Никнейм');
      return;
    }
    
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[index] = {
        ...newQuestions[index],
        question_type: newType,
        options: (newType === 'select' || newType === 'radio' || newType === 'checkbox') 
          ? (newQuestions[index].options || ['Вариант 1', 'Вариант 2'])
          : null
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleOptionsChange = (index, optionsString) => {
    // Проверяем, не пытаемся ли изменить варианты ответов для Discord ID или Никнейм
    const question = formData.questions[index];
    if (question && (question.question_text === 'Discord ID' || question.question_text === 'Никнейм')) {
      setError('Нельзя изменить варианты ответов для системных полей Discord ID и Никнейм');
      return;
    }
    
    const options = optionsString.split('\n').filter(o => o.trim()).map(o => o.trim());
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[index] = {
        ...newQuestions[index],
        options: options.length > 0 ? options : null
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleAddOption = (index, optionIndex) => {
    const question = formData.questions[index];
    if (!question) return;
    
    const currentOptions = question.options || [];
    const newOptions = [...currentOptions];
    
    if (optionIndex !== undefined && optionIndex >= 0) {
      newOptions.splice(optionIndex + 1, 0, '');
    } else {
      newOptions.push('');
    }
    
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[index] = {
        ...newQuestions[index],
        options: newOptions
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleRemoveOption = (index, optionIndex) => {
    const question = formData.questions[index];
    if (!question || !question.options || question.options.length <= 1) return;
    
    const newOptions = question.options.filter((_, i) => i !== optionIndex);
    
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[index] = {
        ...newQuestions[index],
        options: newOptions.length > 0 ? newOptions : ['Вариант 1']
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleOptionTextChange = (index, optionIndex, value) => {
    const question = formData.questions[index];
    if (!question || !question.options) return;
    
    const newOptions = [...question.options];
    newOptions[optionIndex] = value;
    
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[index] = {
        ...newQuestions[index],
        options: newOptions
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleToggleOtherOption = (index) => {
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[index] = {
        ...newQuestions[index],
        has_other_option: !newQuestions[index].has_other_option
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Название заявки обязательно');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const url = editingId 
        ? `/api/applications/${editingId}`
        : '/api/applications';
      
      const method = editingId ? 'PUT' : 'POST';

      // Фильтруем системные вопросы перед отправкой (они добавляются автоматически на сервере)
      const filteredQuestions = formData.questions.filter(
        q => q.question_text !== 'Discord ID' && q.question_text !== 'Никнейм'
      );

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          questions: filteredQuestions
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка при сохранении');
      }

      setSuccess(editingId ? 'Заявка обновлена' : 'Заявка создана');
      setShowForm(false);
      setEditingId(null);
      fetchApplications();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateSubmissionStatus = async (submissionId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/applications/submissions/${submissionId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Ошибка при обновлении статуса');
      }

      setSuccess('Статус обновлён');
      handleViewSubmissions(viewingSubmissions.id);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveSettings = async () => {
    if (!settingsAppId) return;

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const app = await fetchApplication(settingsAppId);
      
      const response = await fetch(`/api/applications/${settingsAppId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: app.title,
          description: app.description || '',
          intro_text: app.intro_text || '',
          completion_text: app.completion_text || '',
          is_open: app.is_open,
          questions: app.questions.filter(q => q.question_text !== 'Discord ID' && q.question_text !== 'Никнейм'),
          auto_close_at: settingsData.auto_close_enabled ? (settingsData.auto_close_at || null) : null,
          allowed_roles: settingsData.restrict_viewers && settingsData.allowed_roles?.length > 0 ? settingsData.allowed_roles : []
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка при сохранении настроек');
      }

      setSuccess('Настройки сохранены');
      setSettingsAppId(null);
      fetchApplications();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Формат даты/времени автозакрытия: "10 марта 2026 в 10:00 МСК"
  const formatAutoCloseDisplay = (isoString) => {
    if (!isoString) return null;
    const closeAt = new Date(isoString);
    if (isNaN(closeAt.getTime())) return null;
    const dateStr = closeAt.toLocaleDateString('ru-RU', {
      timeZone: 'Europe/Moscow',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const timeStr = closeAt.toLocaleTimeString('ru-RU', {
      timeZone: 'Europe/Moscow',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return `${dateStr} в ${timeStr} МСК`;
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      pending: 'status-pending',
      reviewed: 'status-reviewed',
      accepted: 'status-accepted',
      rejected: 'status-rejected'
    };
    return statusMap[status] || 'status-pending';
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      pending: 'Ожидает',
      reviewed: 'Рассмотрена',
      accepted: 'Принята',
      rejected: 'Отклонена'
    };
    return statusMap[status] || status;
  };

  const handleCopyLink = (app) => {
    const slug = app.slug || `application-${app.id}`;
    const link = `${getFormsUrl()}/${slug}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedLinkId(app.id);
      setSuccess('Ссылка скопирована в буфер обмена');
      setTimeout(() => {
        setCopiedLinkId(null);
      }, 2000);
    }).catch(() => {
      setError('Не удалось скопировать ссылку');
    });
  };

  if (loading) {
    return (
      <div className="applications-container">
        <div className="loading-spinner">Загрузка...</div>
      </div>
    );
  }


  return (
    <div className="applications-container">
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      <div className="applications-toolbar">
        <div className="applications-toolbar__title">
          <h2>Заявки</h2>
          <span className="applications-toolbar__meta">
            {applications.length
              ? `${applications.length} ${applications.length === 1 ? 'форма' : applications.length < 5 ? 'формы' : 'форм'}`
              : 'Пока пусто'}
          </span>
        </div>
        <div className="applications-toolbar__actions">
          <button
            type="button"
            className="applications-toolbar__btn applications-toolbar__btn--ghost"
            onClick={() => setShowPresets(true)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            Пресеты
          </button>
          <button
            type="button"
            className="applications-toolbar__btn applications-toolbar__btn--primary"
            onClick={handleAddClick}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Создать
          </button>
        </div>
      </div>

      {applications.length === 0 ? (
        <div className="empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
          </svg>
          <p>Нет заявок. Создайте первую заявку.</p>
        </div>
      ) : (
        <div className="applications-grid">
          {applications.map(app => (
            <div key={app.id} className="application-card">
              <div className="application-card-header">
                <h3>{app.title}</h3>
                <div className="application-header-actions">
                  <div className={`status-badge ${app.is_open ? 'status-open' : 'status-closed'}`}>
                    {app.is_open ? 'Открыта' : 'Закрыта'}
                  </div>
                  <button 
                    className="btn-header-icon btn-preview" 
                    onClick={() => handlePreviewClick(app.id)}
                    title="Предварительный просмотр"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  </button>
                  {app.can_manage !== false && (
                    <button 
                      className="btn-header-icon btn-lock-toggle" 
                      onClick={() => handleToggleOpen(app.id, app.is_open)}
                      title={app.is_open ? 'Закрыть приём' : 'Открыть приём'}
                    >
                      {app.is_open ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                          <circle cx="12" cy="16" r="1"></circle>
                          <path d="M7 11V7a5 5 0 0 1 9.33-2.5"></path>
                        </svg>
                      )}
                    </button>
                  )}
                </div>
              </div>
              
              <p className="application-description">
                Описание: {app.description || 'отсутствует'}
              </p>

              <div className="application-meta">
                <span className="meta-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  {app.submissions_count || 0} заявок
                </span>
                <span className="meta-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  {formatDate(app.created_at)}
                </span>
              </div>

              {app.is_open && app.auto_close_at && (() => {
                const autoClose = formatAutoCloseDisplay(app.auto_close_at);
                if (!autoClose) return null;
                return (
                  <p className="application-auto-close">
                    <span className="application-auto-close-label">Автозакрытие:</span> {autoClose}
                  </p>
                );
              })()}

              <div className="application-link-section">
                <div className="link-field-wrapper">
                  <input
                    type="text"
                    readOnly
                    value={`${getFormsUrl()}/${app.slug || `application-${app.id}`}`}
                    className="link-field"
                    onClick={(e) => e.target.select()}
                  />
                  <button 
                    className="btn-icon btn-secondary link-copy-btn" 
                    onClick={() => handleCopyLink(app)}
                    title={copiedLinkId === app.id ? "Скопировано" : "Копировать ссылку"}
                  >
                    {copiedLinkId === app.id ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="application-actions">
                {app.can_manage !== false ? (
                  <>
                    <button 
                      className="btn-action btn-submissions" 
                      onClick={() => handleViewSubmissions(app.id)}
                      title="Просмотр отправленных заявок"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                      <span>Ответы ({app.submissions_count || 0})</span>
                    </button>
                    <button 
                      className="btn-action btn-edit" 
                      onClick={() => handleEditClick(app.id)}
                      title="Редактировать"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                      <span>Редактировать</span>
                    </button>
                    <button 
                      className="btn-action btn-settings" 
                      onClick={() => handleSettingsClick(app.id)}
                      title="Настройки"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                      <span>Настройки</span>
                    </button>
                    <button 
                      className="btn-action btn-delete" 
                      onClick={() => handleDelete(app.id)}
                      title="Удалить"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                      <span>Удалить</span>
                    </button>
                  </>
                ) : (
                  <span className="application-no-access" style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                    Нет доступа к управлению этой формой
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Модальное окно формы */}
      {showForm && (
        <ApplicationFormModal
          formData={formData}
          editingId={editingId}
          isSubmitting={isSubmitting}
          presets={presets}
          servers={servers}
          onPresetSelect={handlePresetSelect}
          onInputChange={handleInputChange}
          onQuestionChange={handleQuestionChange}
          onAddQuestion={handleAddQuestion}
          onRemoveQuestion={handleRemoveQuestion}
          onMoveQuestion={handleMoveQuestion}
          onQuestionTypeChange={handleQuestionTypeChange}
          onOptionsChange={handleOptionsChange}
          onAddOption={handleAddOption}
          onRemoveOption={handleRemoveOption}
          onOptionTextChange={handleOptionTextChange}
          onToggleOtherOption={handleToggleOtherOption}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowForm(false);
            setEditingId(null);
            setFormData({ title: '', description: '', intro_text: '', completion_text: '', is_open: true, questions: [], preset_id: null, auto_close_at: '', allowed_viewers: [] });
          }}
        />
      )}

      {/* Модальное окно предварительного просмотра */}
      {previewId && (
        <ApplicationPreviewModal
          applicationId={previewId}
          onClose={() => setPreviewId(null)}
        />
      )}

      {/* Модальное окно отправленных заявок */}
      {viewingSubmissions && (
        <SubmissionsModal
          applicationId={viewingSubmissions.id}
          applicationTitle={viewingSubmissions.title}
          submissions={viewingSubmissions.submissions}
          onClose={() => setViewingSubmissions(null)}
          onUpdateStatus={handleUpdateSubmissionStatus}
          onRefresh={() => handleViewSubmissions(viewingSubmissions.id)}
        />
      )}

      {/* Модальное окно управления пресетами */}
      {showPresets && (
        <PresetsModal
          presets={presets}
          onClose={() => setShowPresets(false)}
          onRefresh={fetchPresets}
        />
      )}

      {/* Модальное окно настроек */}
      {settingsAppId && (
        <SettingsModal
          settingsData={settingsData}
          siteRoles={siteRoles}
          isCuratorModerator={isCuratorModerator}
          isSubmitting={isSubmitting}
          onSettingsChange={(field, value) => {
            setSettingsData(prev => ({ ...prev, [field]: value }));
          }}
          onAllowedRoleChange={(roleName, checked) => {
            setSettingsData(prev => {
              const current = prev.allowed_roles || [];
              if (checked) {
                return { ...prev, allowed_roles: [...current, roleName] };
              }
              return { ...prev, allowed_roles: current.filter(r => r !== roleName) };
            });
          }}
          onSave={handleSaveSettings}
          onClose={() => setSettingsAppId(null)}
        />
      )}
    </div>
  );
};

// Компонент модального окна формы
const ApplicationFormModal = ({
  formData,
  editingId,
  isSubmitting,
  presets,
  servers,
  onPresetSelect,
  onInputChange,
  onQuestionChange,
  onAddQuestion,
  onRemoveQuestion,
  onMoveQuestion,
  onQuestionTypeChange,
  onOptionsChange,
  onAddOption,
  onRemoveOption,
  onOptionTextChange,
  onToggleOtherOption,
  onSubmit,
  onClose
}) => {
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [questionTypeSelectOpen, setQuestionTypeSelectOpen] = useState({});
  const questionTypeSelectRefs = useRef({});
  const [scaleMinSelectOpen, setScaleMinSelectOpen] = useState({});
  const [scaleMaxSelectOpen, setScaleMaxSelectOpen] = useState({});
  const scaleMinSelectRefs = useRef({});
  const scaleMaxSelectRefs = useRef({});
  const [draggedQuestionIndex, setDraggedQuestionIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isPresentationExpanded, setIsPresentationExpanded] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  // Сохраняем начальное состояние формы при открытии
  useEffect(() => {
    setInitialFormData(JSON.parse(JSON.stringify(formData)));
  }, []);

  // При загрузке формы (в т.ч. при редактировании) раскрывать блок «Описание» у вопросов, у которых оно уже задано
  const questionsDescriptionFlags = (formData.questions || []).map(q => !!(q.question_description && String(q.question_description).trim()));
  useEffect(() => {
    if (!formData.questions || !formData.questions.length) return;
    setExpandedDescriptions(prev => {
      const next = { ...prev };
      formData.questions.forEach((q, i) => {
        if (q.question_description != null && String(q.question_description).trim()) next[i] = true;
      });
      return next;
    });
  }, [questionsDescriptionFlags.join('')]);

  // Функция проверки изменений
  const hasUnsavedChanges = () => {
    if (!initialFormData) return false;
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  };

  // Обработчик закрытия с проверкой изменений
  const handleCloseClick = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedModal(true);
    } else {
      onClose();
    }
  };

  // Сохранить и закрыть
  const handleSaveAndClose = async () => {
    setShowUnsavedModal(false);
    try {
      const form = document.getElementById('application-form');
      if (form) {
        const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
        form.dispatchEvent(submitEvent);
        // Ждем завершения сохранения
        await new Promise(resolve => setTimeout(resolve, 500));
        onClose();
      } else {
        onClose();
      }
    } catch (err) {
      console.error('Error saving:', err);
      onClose();
    }
  };

  // Закрыть без сохранения
  const handleCloseWithoutSave = () => {
    setShowUnsavedModal(false);
    onClose();
  };

  const handleDragStart = (e, index, realIndex) => {
    // Не позволяем перетаскивать системные вопросы
    if (realIndex < 0) {
      e.preventDefault();
      return;
    }
    setDraggedQuestionIndex(index);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
    }
    // Находим родительский элемент question-item и делаем его полупрозрачным
    const questionItem = e.currentTarget.closest('.question-item');
    if (questionItem) {
      questionItem.style.opacity = '0.5';
    }
  };

  // Touch handlers для мобильных устройств
  const touchStartRef = useRef({ 
    x: 0, 
    y: 0, 
    index: null, 
    realIndex: null, 
    element: null,
    startTime: null,
    holdTimer: null,
    isDragging: false,
    scrollInterval: null
  });

  const handleTouchStart = (e, index, realIndex) => {
    // Не позволяем перетаскивать системные вопросы
    if (realIndex < 0) {
      return;
    }
    
    // Не начинаем перетаскивание, если клик был на кнопке
    const target = e.target;
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      return;
    }

    const touch = e.touches[0];
    const questionItem = e.currentTarget.closest('.question-item');
    
    // Очищаем предыдущий таймер, если есть
    if (touchStartRef.current.holdTimer) {
      clearTimeout(touchStartRef.current.holdTimer);
    }
    
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      index: index,
      realIndex: realIndex,
      element: questionItem,
      startTime: Date.now(),
      holdTimer: null,
      isDragging: false,
      scrollInterval: null
    };
    
    // Запускаем таймер на 1 секунду
    touchStartRef.current.holdTimer = setTimeout(() => {
      // Вибрация при активации
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      touchStartRef.current.isDragging = true;
      
      if (questionItem) {
        questionItem.style.opacity = '0.5';
        questionItem.style.transform = 'scale(1.05)';
        questionItem.style.transition = 'transform 0.2s';
      }
      
      setDraggedQuestionIndex(index);
    }, 1000);
  };

  const handleTouchMove = (e) => {
    if (!touchStartRef.current.element || !touchStartRef.current.isDragging) {
      // Если еще не прошло 1 секунду, отменяем таймер при движении
      const deltaX = Math.abs(e.touches[0].clientX - touchStartRef.current.x);
      const deltaY = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
      if (deltaX > 10 || deltaY > 10) {
        if (touchStartRef.current.holdTimer) {
          clearTimeout(touchStartRef.current.holdTimer);
          touchStartRef.current.holdTimer = null;
        }
      }
      return;
    }
    
    const touch = e.touches[0];
    e.preventDefault(); // Предотвращаем скролл только после активации
    
    // Автоматическая прокрутка при приближении к краям
    const scrollThreshold = 100; // Расстояние от края для начала прокрутки
    const scrollSpeed = 10; // Скорость прокрутки
    
    const container = document.querySelector('.form-modal-body');
    if (container) {
      const containerRect = container.getBoundingClientRect();
      const touchY = touch.clientY;
      
      // Останавливаем предыдущую прокрутку
      if (touchStartRef.current.scrollInterval) {
        clearInterval(touchStartRef.current.scrollInterval);
        touchStartRef.current.scrollInterval = null;
      }
      
      // Прокрутка вверх
      if (touchY < containerRect.top + scrollThreshold) {
        touchStartRef.current.scrollInterval = setInterval(() => {
          if (container.scrollTop > 0) {
            container.scrollTop -= scrollSpeed;
          } else {
            if (touchStartRef.current.scrollInterval) {
              clearInterval(touchStartRef.current.scrollInterval);
              touchStartRef.current.scrollInterval = null;
            }
          }
        }, 16);
      }
      // Прокрутка вниз
      else if (touchY > containerRect.bottom - scrollThreshold) {
        touchStartRef.current.scrollInterval = setInterval(() => {
          if (container.scrollTop < container.scrollHeight - container.clientHeight) {
            container.scrollTop += scrollSpeed;
          } else {
            if (touchStartRef.current.scrollInterval) {
              clearInterval(touchStartRef.current.scrollInterval);
              touchStartRef.current.scrollInterval = null;
            }
          }
        }, 16);
      }
    }
    
    // Находим элемент под пальцем
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
    const questionItemBelow = elementBelow?.closest('.question-item');
    
    // Убираем подсветку со всех элементов
    document.querySelectorAll('.questions-list .question-item').forEach(item => {
      if (item !== touchStartRef.current.element) {
        item.style.border = '';
        item.style.boxShadow = '';
      }
    });
    
    if (questionItemBelow && questionItemBelow !== touchStartRef.current.element) {
      // Визуальная индикация элемента, на который ведется
      questionItemBelow.style.border = '2px solid var(--accent-color)';
      questionItemBelow.style.boxShadow = '0 0 10px rgba(255, 128, 0, 0.5)';
      
      // Находим индекс элемента под пальцем
      const allItems = document.querySelectorAll('.questions-list .question-item');
      let newIndex = -1;
      allItems.forEach((item, idx) => {
        if (item === questionItemBelow) {
          newIndex = idx;
        }
      });
      
      if (newIndex >= 0) {
        // Вычисляем realIndex для элемента под пальцем
        const systemQuestionsCount = 2;
        const newRealIndex = newIndex >= systemQuestionsCount ? newIndex - systemQuestionsCount : -1;
        
        if (newRealIndex >= 0 && newRealIndex !== touchStartRef.current.realIndex) {
          setDragOverIndex(newIndex);
        }
      }
    }
  };

  const handleTouchEnd = (e) => {
    // Очищаем таймер, если перетаскивание не началось
    if (touchStartRef.current.holdTimer) {
      clearTimeout(touchStartRef.current.holdTimer);
      touchStartRef.current.holdTimer = null;
    }
    
    // Останавливаем прокрутку
    if (touchStartRef.current.scrollInterval) {
      clearInterval(touchStartRef.current.scrollInterval);
      touchStartRef.current.scrollInterval = null;
    }
    
    if (!touchStartRef.current.element || !touchStartRef.current.isDragging) {
      // Если перетаскивание не было активировано, просто сбрасываем состояние
      touchStartRef.current = { 
        x: 0, 
        y: 0, 
        index: null, 
        realIndex: null, 
        element: null,
        startTime: null,
        holdTimer: null,
        isDragging: false,
        scrollInterval: null
      };
      return;
    }
    
    const touch = e.changedTouches[0];
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
    const questionItemBelow = elementBelow?.closest('.question-item');
    
    // Убираем визуальные эффекты со всех элементов
    document.querySelectorAll('.questions-list .question-item').forEach(item => {
      item.style.opacity = '1';
      item.style.transform = '';
      item.style.border = '';
      item.style.boxShadow = '';
    });
    
    if (questionItemBelow && questionItemBelow !== touchStartRef.current.element) {
      // Находим индекс элемента под пальцем
      const allItems = document.querySelectorAll('.questions-list .question-item');
      let dropIndex = -1;
      allItems.forEach((item, idx) => {
        if (item === questionItemBelow) {
          dropIndex = idx;
        }
      });
      
      if (dropIndex >= 0) {
        const systemQuestionsCount = 2;
        const dropRealIndex = dropIndex >= systemQuestionsCount ? dropIndex - systemQuestionsCount : -1;
        
        if (dropRealIndex >= 0 && touchStartRef.current.realIndex >= 0) {
          const draggedRealIndex = touchStartRef.current.realIndex;
          
          if (draggedRealIndex !== dropRealIndex) {
            // Перемещаем вопрос
            if (draggedRealIndex < dropRealIndex) {
              // Перемещаем вниз
              for (let i = draggedRealIndex; i < dropRealIndex; i++) {
                onMoveQuestion(i, 'down');
              }
            } else {
              // Перемещаем вверх
              for (let i = draggedRealIndex; i > dropRealIndex; i--) {
                onMoveQuestion(i, 'up');
              }
            }
          }
        }
      }
    }
    
    // Сбрасываем состояние
    touchStartRef.current = { 
      x: 0, 
      y: 0, 
      index: null, 
      realIndex: null, 
      element: null,
      startTime: null,
      holdTimer: null,
      isDragging: false,
      scrollInterval: null
    };
    setDraggedQuestionIndex(null);
    setDragOverIndex(null);
  };

  // Глобальные обработчики touch для мобильных устройств
  useEffect(() => {
    const handleGlobalTouchMove = (e) => {
      if (touchStartRef.current.element) {
        handleTouchMove(e);
      }
    };

    const handleGlobalTouchEnd = (e) => {
      if (touchStartRef.current.element) {
        handleTouchEnd(e);
      }
    };

    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    document.addEventListener('touchend', handleGlobalTouchEnd);

    return () => {
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [formData.questions, onMoveQuestion]); // Зависимость от questions и onMoveQuestion

  const handleDragEnd = (e) => {
    // Восстанавливаем прозрачность родительского элемента question-item
    const questionItem = e.currentTarget.closest('.question-item');
    if (questionItem) {
      questionItem.style.opacity = '1';
    }
    setDraggedQuestionIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index, realIndex) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    
    // Не позволяем перетаскивать на системные вопросы
    if (realIndex < 0 || draggedQuestionIndex === null || draggedQuestionIndex === index) {
      return;
    }
    
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    // Проверяем, что мы действительно покинули элемент вопроса, а не просто перешли на дочерний элемент
    const questionItem = e.currentTarget;
    const relatedTarget = e.relatedTarget;
    if (!questionItem.contains(relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e, dropIndex, dropRealIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);
    
    // Не позволяем перетаскивать на системные вопросы
    if (dropRealIndex < 0 || draggedQuestionIndex === null) {
      setDraggedQuestionIndex(null);
      return;
    }
    
    // Находим реальный индекс перетаскиваемого вопроса
    const draggedRealIndex = draggedQuestionIndex >= 2 ? draggedQuestionIndex - 2 : -1;
    
    if (draggedRealIndex < 0 || draggedRealIndex === dropRealIndex) {
      setDraggedQuestionIndex(null);
      return;
    }
    
    // Восстанавливаем прозрачность перетаскиваемого элемента
    if (draggedQuestionIndex !== null) {
      const allQuestionItems = document.querySelectorAll('.questions-list .question-item');
      if (allQuestionItems[draggedQuestionIndex]) {
        allQuestionItems[draggedQuestionIndex].style.opacity = '1';
      }
    }
    
    // Перемещаем вопрос
    if (draggedRealIndex < dropRealIndex) {
      // Перемещаем вниз
      for (let i = draggedRealIndex; i < dropRealIndex; i++) {
        onMoveQuestion(i, 'down');
      }
    } else {
      // Перемещаем вверх
      for (let i = draggedRealIndex; i > dropRealIndex; i--) {
        onMoveQuestion(i, 'up');
      }
    }
    
    setDraggedQuestionIndex(null);
  };

  // Закрытие выпадающих списков при клике вне их
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Проверяем все открытые списки типа вопроса
      Object.keys(questionTypeSelectOpen).forEach(index => {
        if (questionTypeSelectOpen[index]) {
          const ref = questionTypeSelectRefs.current[index];
          if (ref && !ref.contains(e.target)) {
            setQuestionTypeSelectOpen(prev => ({ ...prev, [index]: false }));
          }
        }
      });
      
      // Проверяем все открытые списки минимального значения
      Object.keys(scaleMinSelectOpen).forEach(index => {
        if (scaleMinSelectOpen[index]) {
          const ref = scaleMinSelectRefs.current[index];
          if (ref && !ref.contains(e.target)) {
            setScaleMinSelectOpen(prev => ({ ...prev, [index]: false }));
          }
        }
      });
      
      // Проверяем все открытые списки максимального значения
      Object.keys(scaleMaxSelectOpen).forEach(index => {
        if (scaleMaxSelectOpen[index]) {
          const ref = scaleMaxSelectRefs.current[index];
          if (ref && !ref.contains(e.target)) {
            setScaleMaxSelectOpen(prev => ({ ...prev, [index]: false }));
          }
        }
      });
    };
    
    const hasOpen = Object.values(questionTypeSelectOpen).some(open => open) ||
                    Object.values(scaleMinSelectOpen).some(open => open) ||
                    Object.values(scaleMaxSelectOpen).some(open => open);
    if (hasOpen) {
      document.addEventListener('click', handleClickOutside, true);
      return () => document.removeEventListener('click', handleClickOutside, true);
    }
  }, [questionTypeSelectOpen, scaleMinSelectOpen, scaleMaxSelectOpen]);

  return (
    <div className="form-modal-overlay">
      <div className="form-modal application-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="form-modal-header">
          <div className="header-content">
            <h3>{editingId ? 'Редактировать заявку' : 'Создать заявку'}</h3>
          </div>
          <div className="header-actions">
            <button 
              type="submit" 
              form="application-form"
              className="btn btn-primary" 
              disabled={isSubmitting}
              style={{ marginRight: '8px' }}
            >
              {isSubmitting ? (editingId ? 'Сохранение...' : 'Создание...') : (editingId ? 'Сохранить' : 'Создать')}
            </button>
            <button type="button" className="btn-close-form" onClick={handleCloseClick} title="Закрыть">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
        <UnsavedChangesModal
          isOpen={showUnsavedModal}
          onClose={() => setShowUnsavedModal(false)}
          onSaveAndClose={handleSaveAndClose}
          onCloseWithoutSave={handleCloseWithoutSave}
        />
        <form id="application-form" onSubmit={onSubmit} className="form-modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'start' }}>
            <div className="form-group">
              <label htmlFor="title">Название заявки *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={onInputChange}
                required
                placeholder="Например: Заявка на пост модератора"
              />
            </div>
            <div className="form-group">
              <label htmlFor="description">Описание</label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description || ''}
                onChange={onInputChange}
                placeholder="Описание заявки (необязательно)"
              />
            </div>
          </div>

          {!editingId && (
            <div className="form-group">
              <label htmlFor="preset_id">Пресет вопросов (необязательно)</label>
              <select
                id="preset_id"
                name="preset_id"
                value={formData.preset_id || ''}
                onChange={(e) => onPresetSelect(e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">Выберите пресет...</option>
                {presets.map(preset => (
                  <option key={preset.id} value={preset.id}>
                    {preset.name} ({preset.questions_count || 0} вопросов)
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="presentation-section">
            <div className="section-header" style={{ cursor: 'pointer' }} onClick={() => setIsPresentationExpanded(!isPresentationExpanded)}>
              <h4>Презентация</h4>
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                style={{ 
                  transform: isPresentationExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  color: 'var(--text-muted)'
                }}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
            {isPresentationExpanded && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label htmlFor="intro_text">Вступительное письмо</label>
                  <textarea
                    id="intro_text"
                    name="intro_text"
                    value={formData.intro_text || ''}
                    onChange={onInputChange}
                    rows="4"
                    placeholder="Вступительное письмо"
                  />
                  <small style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Это письмо будет отображаться перед вопросами в форме заявки. Для форматирования используйте: <strong>**жирный текст**</strong> для жирного шрифта и <strong>__подчеркнутый текст__</strong> для подчеркивания.
                  </small>
                </div>
                <div className="form-group">
                  <label htmlFor="completion_text">Письмо завершения</label>
                  <textarea
                    id="completion_text"
                    name="completion_text"
                    value={formData.completion_text || ''}
                    onChange={onInputChange}
                    rows="4"
                    placeholder="Письмо завершения"
                  />
                  <small style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Это письмо будет отображаться после успешной отправки заявки. Для форматирования используйте: <strong>**жирный текст**</strong> для жирного шрифта и <strong>__подчеркнутый текст__</strong> для подчеркивания.
                  </small>
                </div>
              </div>
            )}
          </div>


          <div className="questions-section">
            <div className="section-header">
              <h4>Вопросы</h4>
            </div>

            {(() => {
              // Всегда создаем системные вопросы для отображения
              const systemQuestions = [
                {
                  question_text: 'Discord ID',
                  question_description: '',
                  question_type: 'text',
                  options: null,
                  is_required: true
                },
                {
                  question_text: 'Никнейм',
                  question_description: '',
                  question_type: 'text',
                  options: null,
                  is_required: true
                }
              ];

              // Фильтруем пользовательские вопросы, убирая системные (если они там есть)
              // Сохраняем порядок вопросов как они есть в formData.questions
              // ВАЖНО: Не сортируем и не меняем порядок - берём вопросы в том порядке, как они есть в formData.questions
              const userQuestions = formData.questions.filter(
                q => q.question_text !== 'Discord ID' && q.question_text !== 'Никнейм'
              );

              // Объединяем: сначала системные, потом пользовательские
              // Порядок userQuestions должен соответствовать порядку в formData.questions
              const allQuestions = [...systemQuestions, ...userQuestions];

              return (
                <div className="questions-list">
                  {userQuestions.length === 0 ? (
                    <div className="empty-questions">
                      <button type="button" className="btn btn-secondary btn-add-question-empty" onClick={() => onAddQuestion()}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        Добавить первый вопрос
                      </button>
                    </div>
                  ) : (
                    <>
                      {allQuestions.map((question, index) => {
                    // Проверяем, является ли вопрос Discord ID или Никнейм - они всегда заблокированы
                    const isDiscordField = question.question_text === 'Discord ID' || question.question_text === 'Никнейм';
                    const isDisabled = isDiscordField;
                    // Реальный индекс вопроса в formData (для системных вопросов -1, так как их нет в formData)
                    // Для пользовательских вопросов вычисляем индекс: index - systemQuestions.length
                    // Важно: realIndex должен соответствовать позиции вопроса в массиве userQuestions
                    const realIndex = isDiscordField ? -1 : index - systemQuestions.length;
                    // Используем стабильный key: для системных вопросов - их текст, для пользовательских - их ID или позиция в formData.questions
                    // Используем realIndex для key, чтобы гарантировать правильный порядок
                    const questionKey = isDiscordField 
                      ? `system-${question.question_text}` 
                      : (question.id ? `user-${question.id}` : `user-${realIndex}-${question.question_text || 'new'}`);
                    
                    return (
                      <div 
                        key={questionKey} 
                        className={`question-item ${draggedQuestionIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
                        onDragOver={(e) => handleDragOver(e, index, realIndex)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index, realIndex)}
                        style={isDisabled ? { opacity: 0.7, backgroundColor: 'var(--bg-secondary)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' } : {}}
                      >
                      <div 
                        className="question-header"
                        draggable={!isDisabled && realIndex >= 0}
                        onDragStart={(e) => {
                          // Не начинаем перетаскивание, если клик был на кнопке
                          const target = e.target;
                          if (target.tagName === 'BUTTON' || target.closest('button')) {
                            e.preventDefault();
                            return false;
                          }
                          
                          handleDragStart(e, index, realIndex);
                        }}
                        onDragEnd={handleDragEnd}
                        onTouchStart={(e) => handleTouchStart(e, index, realIndex)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        style={realIndex >= 0 && !isDisabled ? { cursor: 'grab', touchAction: 'none' } : {}}
                      >
                        <span className="question-number">
                          {realIndex >= 0 && (
                            <svg 
                              width="16" 
                              height="16" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2"
                              style={{ 
                                marginRight: '8px', 
                                display: 'inline-block',
                                verticalAlign: 'middle',
                                cursor: 'grab',
                                color: 'var(--text-muted)'
                              }}
                            >
                              <circle cx="9" cy="12" r="1"></circle>
                              <circle cx="9" cy="5" r="1"></circle>
                              <circle cx="9" cy="19" r="1"></circle>
                              <circle cx="15" cy="12" r="1"></circle>
                              <circle cx="15" cy="5" r="1"></circle>
                              <circle cx="15" cy="19" r="1"></circle>
                            </svg>
                          )}
                          Вопрос {index + 1}
                          {isDiscordField && <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '8px' }}>(системное поле, заблокировано)</span>}
                        </span>
                      <div className="question-actions">
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => {
                            if (realIndex >= 0) {
                              onAddQuestion(realIndex);
                            }
                          }}
                          disabled={isDisabled || realIndex < 0}
                          title={isDisabled ? 'Нельзя добавить' : 'Добавить вопрос после этого'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="btn-icon btn-danger"
                          onClick={() => realIndex >= 0 && onRemoveQuestion(realIndex)}
                          disabled={isDisabled || realIndex < 0}
                          title={isDisabled ? 'Нельзя удалить' : 'Удалить вопрос'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className="form-group question-main-fields">
                      <div className="question-text-field">
                        <label>Текст вопроса *</label>
                        <input
                          type="text"
                          value={question.question_text}
                          onChange={(e) => realIndex >= 0 && onQuestionChange(realIndex, 'question_text', e.target.value)}
                          required
                          placeholder="Введите вопрос"
                          disabled={isDisabled}
                          readOnly={isDisabled}
                          style={isDisabled ? { cursor: 'not-allowed', backgroundColor: 'var(--bg-tertiary)' } : {}}
                        />
                      </div>
                      <div className="question-type-field">
                        <label>Тип вопроса</label>
                        <div className={`question-type-select-wrap ${questionTypeSelectOpen[index] ? 'open' : ''}`} ref={el => questionTypeSelectRefs.current[index] = el}>
                          <div
                            className={`question-type-select-custom ${questionTypeSelectOpen[index] ? 'open' : ''} ${isDisabled ? 'disabled' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isDisabled) {
                                const isCurrentlyOpen = questionTypeSelectOpen[index];
                                // Закрываем все открытые списки, затем открываем текущий, если он был закрыт
                                setQuestionTypeSelectOpen(prev => {
                                  const newState = {};
                                  // Если текущий был открыт, просто закрываем его
                                  if (isCurrentlyOpen) {
                                    return { ...prev, [index]: false };
                                  }
                                  // Иначе закрываем все и открываем текущий
                                  return { [index]: true };
                                });
                              }
                            }}
                            role="combobox"
                            aria-expanded={questionTypeSelectOpen[index]}
                            aria-haspopup="listbox"
                          >
                            <span className="question-type-select-value">
                              {(() => {
                                const typeLabels = {
                                  'text': 'Текст (одна строка)',
                                  'textarea': 'Текст (много строк)',
                                  'select': 'Выпадающий список',
                                  'radio': 'Один вариант',
                                  'checkbox': 'Несколько вариантов',
                                  'server': 'Выбор сервера',
                                  'scale': 'Шкала'
                                };
                                return typeLabels[question.question_type] || question.question_type;
                              })()}
                            </span>
                            <span className="question-type-select-arrow">▾</span>
                          </div>
                          {questionTypeSelectOpen[index] && !isDisabled && (
                            <div className="question-type-select-dropdown" role="listbox">
                              {[
                                { value: 'text', label: 'Текст (одна строка)' },
                                { value: 'textarea', label: 'Текст (много строк)' },
                                { value: 'select', label: 'Выпадающий список' },
                                { value: 'radio', label: 'Один вариант' },
                                { value: 'checkbox', label: 'Несколько вариантов' },
                                { value: 'server', label: 'Выбор сервера' },
                                { value: 'scale', label: 'Шкала' }
                              ].map((type) => (
                                <div
                                  key={type.value}
                                  className={`question-type-select-option ${question.question_type === type.value ? 'selected' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (realIndex >= 0) {
                                      onQuestionTypeChange(realIndex, type.value);
                                    }
                                    setQuestionTypeSelectOpen(prev => ({ ...prev, [index]: false }));
                                  }}
                                  role="option"
                                >
                                  <span className="question-type-select-option-text">{type.label}</span>
                                  {question.question_type === type.value && <span className="question-type-select-option-check">✓</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {(question.question_type === 'select' || 
                      question.question_type === 'radio' || 
                      question.question_type === 'checkbox') && (
                      <div className="form-group">
                        <label>Варианты ответов *</label>
                        <div className="options-list" style={{ marginTop: '8px' }}>
                          {question.options && question.options.length > 0 ? (
                            question.options.map((option, optIndex) => (
                              <div key={optIndex} className="option-item" style={{ 
                                display: 'flex', 
                                gap: '8px', 
                                alignItems: 'center', 
                                marginBottom: '8px',
                                padding: '8px',
                                background: 'var(--bg-secondary)',
                                borderRadius: '6px',
                                border: '1px solid var(--border-color)'
                              }}>
                                <input
                                  type="text"
                                  value={option}
                                  onChange={(e) => realIndex >= 0 && onOptionTextChange(realIndex, optIndex, e.target.value)}
                                  placeholder={`Вариант ${optIndex + 1}`}
                                  disabled={isDisabled}
                                  readOnly={isDisabled}
                                  style={{ 
                                    flex: 1,
                                    padding: '8px 12px',
                                    background: isDisabled ? 'var(--bg-tertiary)' : 'var(--bg-primary)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '4px',
                                    color: 'var(--text-primary)',
                                    fontSize: '14px',
                                    cursor: isDisabled ? 'not-allowed' : 'text'
                                  }}
                                />
                                {!isDisabled && (
                                  <>
                                    <button
                                      type="button"
                                      className="btn-icon"
                                      onClick={() => realIndex >= 0 && onAddOption(realIndex, optIndex)}
                                      title="Добавить вариант после этого"
                                      style={{
                                        padding: '6px',
                                        background: 'transparent',
                                        border: 'none',
                                        color: 'var(--text-secondary)',
                                        cursor: 'pointer',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="12" y1="5" x2="12" y2="19"></line>
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                      </svg>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-icon"
                                      onClick={() => realIndex >= 0 && onRemoveOption(realIndex, optIndex)}
                                      disabled={question.options.length <= 1}
                                      title="Удалить вариант"
                                      style={{
                                        padding: '6px',
                                        background: 'transparent',
                                        border: 'none',
                                        color: question.options.length <= 1 ? 'var(--text-disabled)' : 'var(--text-danger, #ef4444)',
                                        cursor: question.options.length <= 1 ? 'not-allowed' : 'pointer',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="3 6 5 6 21 6"></polyline>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                      </svg>
                                    </button>
                                  </>
                                )}
                              </div>
                            ))
                          ) : (
                            <div style={{ color: 'var(--text-muted)', fontSize: '14px', padding: '12px', textAlign: 'center' }}>
                              Нет вариантов ответов
                            </div>
                          )}
                          {!isDisabled && (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => {
                                if (realIndex >= 0) {
                                  onAddOption(realIndex);
                                }
                              }}
                              style={{
                                marginTop: '8px',
                                width: '100%',
                                padding: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                              </svg>
                              Добавить вариант
                            </button>
                          )}
                        </div>
                        {question.question_type === 'radio' && (
                          <div className="form-group" style={{ marginTop: '12px' }}>
                            <label className="toggle-label" style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={question.has_other_option || false}
                                onChange={() => realIndex >= 0 && onToggleOtherOption(realIndex)}
                                className="toggle-input"
                                disabled={isDisabled}
                              />
                              <span className="toggle-switch"></span>
                              <span className="toggle-text">Добавить "Другое"</span>
                            </label>
                          </div>
                        )}
                      </div>
                    )}

                    {question.question_type === 'server' && (
                      <div className="form-group">
                        <label>Выбор сервера</label>
                        <div className="server-select-wrap">
                          <div className="server-select-custom placeholder" style={isDisabled ? { cursor: 'not-allowed', opacity: 0.6, pointerEvents: 'none' } : {}}>
                            <span className="server-select-value">
                              <span className="server-select-placeholder">Список серверов загружается из настроек</span>
                            </span>
                            <span className="server-select-arrow">▾</span>
                          </div>
                        </div>
                        <small style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          При заполнении заявки пользователь сможет выбрать сервер из списка в настройках
                        </small>
                      </div>
                    )}

                    {question.question_type === 'scale' && (
                      <div className="form-group">
                        <div className="question-scale-settings-row">
                          <div>
                            <label>Минимальное значение *</label>
                            <div className={`question-type-select-wrap ${scaleMinSelectOpen[index] ? 'open' : ''}`} ref={el => scaleMinSelectRefs.current[index] = el}>
                              <div
                                className={`question-type-select-custom ${scaleMinSelectOpen[index] ? 'open' : ''} ${isDisabled ? 'disabled' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isDisabled) {
                                    const isCurrentlyOpen = scaleMinSelectOpen[index];
                                    setScaleMinSelectOpen(prev => {
                                      if (isCurrentlyOpen) {
                                        return { ...prev, [index]: false };
                                      }
                                      return { [index]: true };
                                    });
                                    // Закрываем другие открытые списки
                                    setScaleMaxSelectOpen({});
                                    setQuestionTypeSelectOpen({});
                                  }
                                }}
                                role="combobox"
                                aria-expanded={scaleMinSelectOpen[index]}
                                aria-haspopup="listbox"
                              >
                                <span className="question-type-select-value">
                                  {question.options?.min !== undefined ? question.options.min : 1}
                                </span>
                                <span className="question-type-select-arrow">▾</span>
                              </div>
                              {scaleMinSelectOpen[index] && !isDisabled && (
                                <div className="question-type-select-dropdown" role="listbox">
                                  {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                                    <div
                                      key={num}
                                      className={`question-type-select-option ${question.options?.min === num ? 'selected' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const min = num;
                                        const max = question.options?.max || 5;
                                        if (realIndex >= 0) {
                                          onQuestionChange(realIndex, 'options', {
                                            ...question.options,
                                            min: min,
                                            max: min >= max ? Math.min(min + 1, 10) : max,
                                            label_min: question.options?.label_min || '',
                                            label_max: question.options?.label_max || ''
                                          });
                                        }
                                        setScaleMinSelectOpen(prev => ({ ...prev, [index]: false }));
                                      }}
                                      role="option"
                                    >
                                      <span className="question-type-select-option-text">{num}</span>
                                      {question.options?.min === num && <span className="question-type-select-option-check">✓</span>}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <label>Подпись для минимального значения</label>
                            <input
                              type="text"
                              value={question.options?.label_min || ''}
                              onChange={(e) => {
                                if (realIndex >= 0) {
                                  onQuestionChange(realIndex, 'options', {
                                    ...question.options,
                                    min: question.options?.min || 1,
                                    max: question.options?.max || 5,
                                    label_min: e.target.value,
                                    label_max: question.options?.label_max || ''
                                  });
                                }
                              }}
                              placeholder="Например: Плохо"
                              disabled={isDisabled}
                              style={{ width: '100%', padding: '8px 12px' }}
                            />
                          </div>
                        </div>
                        <div className="question-scale-settings-row">
                          <div>
                            <label>Максимальное значение *</label>
                            <div className={`question-type-select-wrap ${scaleMaxSelectOpen[index] ? 'open' : ''}`} ref={el => scaleMaxSelectRefs.current[index] = el}>
                              <div
                                className={`question-type-select-custom ${scaleMaxSelectOpen[index] ? 'open' : ''} ${isDisabled ? 'disabled' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isDisabled) {
                                    const isCurrentlyOpen = scaleMaxSelectOpen[index];
                                    setScaleMaxSelectOpen(prev => {
                                      if (isCurrentlyOpen) {
                                        return { ...prev, [index]: false };
                                      }
                                      return { [index]: true };
                                    });
                                    // Закрываем другие открытые списки
                                    setScaleMinSelectOpen({});
                                    setQuestionTypeSelectOpen({});
                                  }
                                }}
                                role="combobox"
                                aria-expanded={scaleMaxSelectOpen[index]}
                                aria-haspopup="listbox"
                              >
                                <span className="question-type-select-value">
                                  {question.options?.max !== undefined ? question.options.max : 5}
                                </span>
                                <span className="question-type-select-arrow">▾</span>
                              </div>
                              {scaleMaxSelectOpen[index] && !isDisabled && (
                                <div className="question-type-select-dropdown" role="listbox">
                                  {Array.from({ length: 10 }, (_, i) => i + 1)
                                    .filter(num => num > (question.options?.min || 1))
                                    .map((num) => (
                                      <div
                                        key={num}
                                        className={`question-type-select-option ${question.options?.max === num ? 'selected' : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const max = num;
                                          const min = question.options?.min || 1;
                                          if (realIndex >= 0) {
                                            onQuestionChange(realIndex, 'options', {
                                              ...question.options,
                                              min: min,
                                              max: max <= min ? Math.min(min + 1, 10) : max,
                                              label_min: question.options?.label_min || '',
                                              label_max: question.options?.label_max || ''
                                            });
                                          }
                                          setScaleMaxSelectOpen(prev => ({ ...prev, [index]: false }));
                                        }}
                                        role="option"
                                      >
                                        <span className="question-type-select-option-text">{num}</span>
                                        {question.options?.max === num && <span className="question-type-select-option-check">✓</span>}
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <label>Подпись для максимального значения</label>
                            <input
                              type="text"
                              value={question.options?.label_max || ''}
                              onChange={(e) => {
                                if (realIndex >= 0) {
                                  onQuestionChange(realIndex, 'options', {
                                    ...question.options,
                                    min: question.options?.min || 1,
                                    max: question.options?.max || 5,
                                    label_min: question.options?.label_min || '',
                                    label_max: e.target.value
                                  });
                                }
                              }}
                              placeholder="Например: Отлично"
                              disabled={isDisabled}
                              style={{ width: '100%', padding: '8px 12px' }}
                            />
                          </div>
                        </div>
                        <small style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          Пользователь сможет выбрать значение от {question.options?.min || 1} до {question.options?.max || 5} на шкале
                        </small>
                      </div>
                    )}

                    <div className="form-group">
                      <div className="question-toggles-divider" aria-hidden="true"></div>
                      <div className="question-toggles-row">
                        <label className="toggle-label" style={isDisabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
                          <input
                            type="checkbox"
                            checked={question.is_required}
                            onChange={(e) => realIndex >= 0 && onQuestionChange(realIndex, 'is_required', e.target.checked)}
                            className="toggle-input"
                            disabled={isDisabled}
                          />
                          <span className="toggle-switch"></span>
                          <span className="toggle-text">{question.is_required ? 'Обязательный вопрос' : 'Необязательный вопрос'}</span>
                        </label>
                        <label className="toggle-label" style={isDisabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
                          <input
                            type="checkbox"
                            checked={expandedDescriptions[index] || false}
                            onChange={(e) => setExpandedDescriptions(prev => ({
                              ...prev,
                              [index]: e.target.checked
                            }))}
                            className="toggle-input"
                            disabled={isDisabled}
                          />
                          <span className="toggle-switch"></span>
                          <span className="toggle-text">Добавить описание к вопросу</span>
                        </label>
                      </div>
                      {expandedDescriptions[index] && (
                        <>
                          <textarea
                            value={question.question_description || ''}
                            onChange={(e) => realIndex >= 0 && onQuestionChange(realIndex, 'question_description', e.target.value)}
                            rows="3"
                            placeholder="Дополнительное описание или подсказка к вопросу"
                            disabled={isDisabled}
                            readOnly={isDisabled}
                            style={{ marginTop: '8px', ...(isDisabled ? { cursor: 'not-allowed', backgroundColor: 'var(--bg-tertiary)' } : {}) }}
                          />
                          {!isDisabled && (
                            <small style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                              Для форматирования используйте: <strong>**жирный текст**</strong> для жирного шрифта и <strong>__подчеркнутый текст__</strong> для подчеркивания. Описание будет отображаться жирным шрифтом.
                            </small>
                          )}
                        </>
                      )}
                    </div>
                    </div>
                    );
                  })}
                    </>
                  )}
                </div>
              );
            })()}
          </div>

        </form>
      </div>
    </div>
  );
};

// Функция для форматирования текста (жирный и подчеркивание)
const formatText = (text) => {
  if (!text) return '';
  
  // Заменяем **текст** на <strong>текст</strong> для жирного
  let formatted = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Заменяем __текст__ на <u>текст</u> для подчеркивания
  formatted = formatted.replace(/__(.*?)__/g, '<u>$1</u>');
  
  return formatted;
};

// Компонент предварительного просмотра
const ApplicationPreviewModal = ({ applicationId, onClose }) => {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchApp = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/applications/${applicationId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Ошибка при загрузке заявки');
        }

        const data = await response.json();
        setApplication(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchApp();
  }, [applicationId]);

  if (loading) {
    return (
      <div className="form-modal-overlay">
        <div className="form-modal" onClick={(e) => e.stopPropagation()}>
          <div className="form-modal-body">
            <div className="loading-spinner">Загрузка...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!application) {
    return null;
  }

  return (
    <div className="form-modal-overlay">
      <div className="form-modal preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="form-modal-header">
          <h3>Предварительный просмотр</h3>
          <button type="button" className="btn-close-form" onClick={onClose} title="Закрыть">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="form-modal-body preview-body">
          <div className="preview-application">
            <h2>{application.title}</h2>
            <p className="preview-description">Описание: {application.description || 'отсутствует'}</p>
            
            {application.intro_text && (
              <div className="preview-intro-text">
                {application.intro_text.split('\n').map((line, i) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: formatText(line) }}></p>
                ))}
              </div>
            )}
            
            <div className="preview-questions">
              {application.questions.map((question, index) => (
                <div key={question.id || index} className="preview-question">
                  <label className="preview-question-label">
                    {question.question_text}
                    {question.is_required && <span className="required-mark"> *</span>}
                  </label>
                  
                  {question.question_description && (
                    <div className="preview-question-description" dangerouslySetInnerHTML={{ __html: formatText(question.question_description) }}></div>
                  )}
                  
                  {question.question_type === 'text' && (
                    <input type="text" disabled placeholder="Текст ответа" />
                  )}
                  
                  {question.question_type === 'textarea' && (
                    <textarea rows="4" disabled placeholder="Текст ответа"></textarea>
                  )}
                  
                  {question.question_type === 'select' && (
                    <select disabled>
                      <option>Выберите вариант</option>
                      {question.options && (typeof question.options === 'string' 
                        ? JSON.parse(question.options) 
                        : question.options).map((opt, i) => (
                        <option key={i}>{opt}</option>
                      ))}
                    </select>
                  )}
                  
                  {question.question_type === 'radio' && (
                    <div className="preview-radio-group">
                      {question.options && (typeof question.options === 'string' 
                        ? JSON.parse(question.options) 
                        : question.options).map((opt, i) => (
                        <label key={i} className="radio-label">
                          <input type="radio" name={`preview-${question.id}`} disabled />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  
                  {question.question_type === 'checkbox' && (
                    <div className="preview-checkbox-group">
                      {question.options && (typeof question.options === 'string' 
                        ? JSON.parse(question.options) 
                        : question.options).map((opt, i) => (
                        <label key={i} className="checkbox-label">
                          <input type="checkbox" disabled />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  
                  {question.question_type === 'server' && (
                    <div className="preview-server-select">
                      <select disabled>
                        <option>Выберите сервер</option>
                      </select>
                      <small style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Список серверов будет загружен при заполнении формы
                      </small>
                    </div>
                  )}
                  
                  {question.question_type === 'scale' && (() => {
                    const scaleOptions = question.options 
                      ? (typeof question.options === 'string' ? JSON.parse(question.options) : question.options)
                      : { min: 1, max: 5, label_min: '', label_max: '' };
                    const min = scaleOptions.min || 1;
                    const max = scaleOptions.max || 5;
                    const labelMin = scaleOptions.label_min || '';
                    const labelMax = scaleOptions.label_max || '';
                    const scaleValues = Array.from({ length: max - min + 1 }, (_, i) => min + i);

                    return (
                      <div className="preview-scale-group">
                        <div className="preview-scale-labels">
                          {labelMin && (
                            <span className="preview-scale-label-min">{labelMin}</span>
                          )}
                          {labelMax && (
                            <span className="preview-scale-label-max">{labelMax}</span>
                          )}
                        </div>
                        <div className="preview-scale-buttons">
                          {scaleValues.map((value) => (
                            <button
                              key={value}
                              type="button"
                              className="preview-scale-button"
                              disabled
                            >
                              {value}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
            
            {application.completion_text && (
              <div className="preview-completion-text">
                {application.completion_text.split('\n').map((line, i) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: formatText(line) }}></p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Компонент модального окна отправленных заявок
const SubmissionsModal = ({ applicationId, applicationTitle, submissions, onClose, onUpdateStatus, onRefresh }) => {
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [servers, setServers] = useState([]);
  const tableContainerRef = useRef(null);
  const [tableWidth, setTableWidth] = useState(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const [viewMode, setViewMode] = useState('byUsers'); // byUsers | byQuestions

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handle = () => setIsMobile(mql.matches);
    mql.addEventListener('change', handle);
    handle();
    return () => mql.removeEventListener('change', handle);
  }, []);

  useEffect(() => {
    // Загружаем список серверов для отображения в ответах
    const fetchServers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/servers', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setServers(data);
        }
      } catch (err) {
        console.error('Error fetching servers:', err);
      }
    };
    fetchServers();
  }, []);

  // Фиксируем ширину таблицы при закрытых деталях, чтобы при раскрытии она не сжималась
  useLayoutEffect(() => {
    if (!selectedSubmission && tableContainerRef.current && submissions.length > 0) {
      const w = tableContainerRef.current.offsetWidth;
      if (w > 0) setTableWidth(w);
    }
  }, [selectedSubmission, submissions.length]);

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      pending: 'status-pending',
      reviewed: 'status-reviewed',
      accepted: 'status-accepted',
      rejected: 'status-rejected'
    };
    return statusMap[status] || 'status-pending';
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      pending: 'Ожидает',
      reviewed: 'Рассмотрена',
      accepted: 'Принята',
      rejected: 'Отклонена'
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Функция для форматирования ответа в зависимости от типа вопроса
  const formatAnswer = (answer) => {
    if (!answer) return '—';

    // Для типа server пытаемся найти сервер в списке по ID
    if (answer.question_type === 'server') {
      let serverId = null;
      
      // Пытаемся получить ID из answer_value
      if (answer.answer_value) {
        try {
          const parsed = typeof answer.answer_value === 'string' 
            ? JSON.parse(answer.answer_value) 
            : answer.answer_value;
          serverId = Array.isArray(parsed) ? parsed[0] : parsed;
        } catch (e) {
          // Если не удалось распарсить, пробуем использовать как есть
          serverId = answer.answer_value;
        }
      }
      
      // Если не нашли в answer_value, пробуем из answer_text (может быть просто ID как строка)
      if (!serverId && answer.answer_text) {
        const textId = answer.answer_text.trim();
        // Проверяем, является ли answer_text числом (ID сервера)
        if (/^\d+$/.test(textId)) {
          serverId = textId;
        }
      }
      
      // Ищем сервер по ID
      if (serverId) {
        const server = servers.find(s => String(s.id) === String(serverId));
        if (server) {
          return (
            <span className="answer-server">
              <ServerIcon server={server} className="answer-server-icon" size={20} />
              <span>RU{server.server_number}_{server.server_name}</span>
            </span>
          );
        }
      }
      
      // Если сервер не найден, показываем что было сохранено
      return answer.answer_text || String(answer.answer_value || '—');
    }

    // Для типа scale — отображаем значение шкалы в виде бейджа
    if (answer.question_type === 'scale') {
      let scaleVal = null;
      if (answer.answer_value !== undefined && answer.answer_value !== null && answer.answer_value !== '') {
        try {
          const parsed = typeof answer.answer_value === 'string'
            ? JSON.parse(answer.answer_value)
            : answer.answer_value;
          scaleVal = typeof parsed === 'number' ? parsed : parseInt(parsed, 10);
        } catch (e) {
          scaleVal = parseInt(String(answer.answer_value), 10);
        }
      }
      if (scaleVal == null && answer.answer_text !== undefined && answer.answer_text !== '') {
        scaleVal = parseInt(String(answer.answer_text).trim(), 10);
      }
      if (scaleVal != null && !Number.isNaN(scaleVal)) {
        return (
          <span className="answer-scale-value" title={`Выбрано: ${scaleVal}`}>
            {scaleVal}
          </span>
        );
      }
      return '—';
    }

    // Если есть answer_text, используем его
    if (answer.answer_text) {
      // Для массивов (checkbox, multiple select) показываем как список
      if (answer.question_type === 'checkbox' && answer.answer_value) {
        try {
          const values = typeof answer.answer_value === 'string' 
            ? JSON.parse(answer.answer_value) 
            : answer.answer_value;
          if (Array.isArray(values)) {
            return (
              <ul className="answer-list">
                {values.map((val, idx) => (
                  <li key={idx}>{val}</li>
                ))}
              </ul>
            );
          }
        } catch (e) {
          // Если не удалось распарсить, показываем как есть
        }
      }
      
      return answer.answer_text;
    }

    // Если есть только answer_value, пытаемся его распарсить
    if (answer.answer_value) {
      try {
        const value = typeof answer.answer_value === 'string' 
          ? JSON.parse(answer.answer_value) 
          : answer.answer_value;
        
        if (Array.isArray(value)) {
          return (
            <ul className="answer-list">
              {value.map((val, idx) => (
                <li key={idx}>{val}</li>
              ))}
            </ul>
          );
        }
        
        return String(value);
      } catch (e) {
        return String(answer.answer_value);
      }
    }

    return '—';
  };

  // Функция для получения текстового значения ответа (для экспорта)
  const getAnswerText = (answer) => {
    if (!answer) return '—';

    // Для типа server пытаемся найти сервер в списке по ID
    if (answer.question_type === 'server') {
      let serverId = null;
      
      if (answer.answer_value) {
        try {
          const parsed = typeof answer.answer_value === 'string' 
            ? JSON.parse(answer.answer_value) 
            : answer.answer_value;
          serverId = Array.isArray(parsed) ? parsed[0] : parsed;
        } catch (e) {
          serverId = answer.answer_value;
        }
      }
      
      if (!serverId && answer.answer_text) {
        const textId = answer.answer_text.trim();
        if (/^\d+$/.test(textId)) {
          serverId = textId;
        }
      }
      
      if (serverId) {
        const server = servers.find(s => String(s.id) === String(serverId));
        if (server) {
          return `RU${server.server_number}_${server.server_name}`;
        }
      }
      
      return answer.answer_text || String(answer.answer_value || '—');
    }

    if (answer.answer_text) {
      if (answer.question_type === 'checkbox' && answer.answer_value) {
        try {
          const values = typeof answer.answer_value === 'string' 
            ? JSON.parse(answer.answer_value) 
            : answer.answer_value;
          if (Array.isArray(values)) {
            return values.join(', ');
          }
        } catch (e) {
          // Игнорируем ошибку
        }
      }
      return answer.answer_text;
    }

    if (answer.answer_value) {
      try {
        const value = typeof answer.answer_value === 'string' 
          ? JSON.parse(answer.answer_value) 
          : answer.answer_value;
        
        if (Array.isArray(value)) {
          return value.join(', ');
        }
        
        return String(value);
      } catch (e) {
        return String(answer.answer_value);
      }
    }

    return '—';
  };

  // Функция экспорта в Excel
  const handleExportToExcel = () => {
    if (!submissions || submissions.length === 0) {
      return;
    }

    // Собираем все уникальные вопросы из всех заявок
    const allQuestions = new Map();
    submissions.forEach(submission => {
      if (submission.answers && Array.isArray(submission.answers)) {
        submission.answers.forEach(answer => {
          if (!allQuestions.has(answer.question_id)) {
            allQuestions.set(answer.question_id, {
              id: answer.question_id,
              text: answer.question_text,
              type: answer.question_type
            });
          }
        });
      }
    });

    const questionsArray = Array.from(allQuestions.values());
    
    // Создаем заголовки: первые две колонки - "Discord ID" и "Никнейм", затем все вопросы (кроме первых двух системных)
    const headers = ['Discord ID', 'Никнейм', ...questionsArray.filter(q => q.text !== 'Discord ID' && q.text !== 'Никнейм').map(q => q.text)];

    // Создаем данные таблицы
    const data = [headers];
    
    // Для каждого пользователя создаем строку
    submissions.forEach(submission => {
      // Извлекаем Discord ID и Никнейм из ответов
      const discordId = submission.discord_id_from_answer || submission.discord_id || submission.submitted_by_discord_id || 'Не указан';
      const nickname = submission.nickname_from_answer || submission.discord_tag || submission.submitted_by_discord_tag || submission.submitted_by_username || submission.discord_username || 'Не указан';
      
      const row = [discordId, nickname];
      
      // Для каждого вопроса (кроме первых двух системных) получаем ответ этого пользователя
      questionsArray
        .filter(q => q.text !== 'Discord ID' && q.text !== 'Никнейм')
        .forEach(question => {
          const answer = submission.answers?.find(a => a.question_id === question.id);
          const answerText = answer ? getAnswerText(answer) : '—';
          row.push(answerText);
        });
      
      data.push(row);
    });

    // Создаем рабочую книгу Excel
    const ws = XLSX.utils.aoa_to_sheet(data);
    
    // Устанавливаем ширину колонок
    const filteredQuestions = questionsArray.filter(q => q.text !== 'Discord ID' && q.text !== 'Никнейм');
    const colWidths = [
      { wch: 20 }, // Колонка с Discord ID
      { wch: 25 }, // Колонка с Никнейм
      ...filteredQuestions.map(() => ({ wch: 30 })) // Колонки с вопросами
    ];
    ws['!cols'] = colWidths;

    // Создаем книгу и добавляем лист
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Заявки');

    // Имя файла: "Название заявки - дата МСК - время МСК.xlsx"
    const now = new Date();
    const moscowDate = now.toLocaleDateString('ru-RU', { timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric' });
    const moscowTime = now.toLocaleTimeString('ru-RU', { timeZone: 'Europe/Moscow', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).replace(/:/g, '-');
    const safeTitle = (applicationTitle || 'Заявки').replace(/[/\\:*?"<>|]/g, '-').trim() || 'Заявки';
    const fileName = `${safeTitle} - ${moscowDate} МСК - ${moscowTime} МСК.xlsx`;

    // Сохраняем файл
    XLSX.writeFile(wb, fileName);
  };

  // Агрегация ответов по вопросам для режима «Отображение по вопросам»
  const questionsStats = React.useMemo(() => {
    if (!submissions || submissions.length === 0) return [];

    const incrementOption = (map, label) => {
      const key = label === undefined || label === null || label === '' ? '—' : String(label);
      map.set(key, (map.get(key) || 0) + 1);
    };

    const questionsMap = new Map();

    submissions.forEach((submission) => {
      const list = submission.questions_with_answers && submission.questions_with_answers.length > 0
        ? submission.questions_with_answers
        : (submission.answers || []);

      list.forEach((q) => {
        const questionId = q.question_id || q.id;
        if (!questionId || !q.question_text) return;
        if (q.question_text === 'Discord ID' || q.question_text === 'Никнейм') return;

        let entry = questionsMap.get(questionId);
        if (!entry) {
          entry = {
            id: questionId,
            text: q.question_text,
            type: q.question_type,
            is_deleted: q.is_deleted === true,
            totalAnswers: 0,
            optionsMap: new Map()
          };
          questionsMap.set(questionId, entry);
        }

        const answerObj = q.answer || q;
        const hasAnyAnswer =
          q.has_answer ||
          !!answerObj.answer_text ||
          !!answerObj.answer_value;
        if (!hasAnyAnswer) {
          return;
        }

        entry.totalAnswers += 1;

        // Считаем распределение только для дискретных типов, текстовые оставляем без круговой диаграммы
        if (q.question_type === 'checkbox') {
          let values = null;
          const raw = answerObj.answer_value;
          if (raw) {
            try {
              const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
              if (Array.isArray(parsed)) {
                values = parsed;
              }
            } catch (e) {
              // игнорируем, попробуем использовать answer_text
            }
          }
          if (!values && answerObj.answer_text) {
            values = answerObj.answer_text.split(',').map(v => v.trim()).filter(Boolean);
          }
          if (Array.isArray(values)) {
            values.forEach((v) => incrementOption(entry.optionsMap, v));
          }
        } else if (['radio', 'select', 'server', 'scale'].includes(q.question_type)) {
          let val = answerObj.answer_text;
          if (!val && answerObj.answer_value) {
            const raw = answerObj.answer_value;
            if (typeof raw === 'string') {
              try {
                const parsed = JSON.parse(raw);
                val = Array.isArray(parsed) ? parsed[0] : String(parsed);
              } catch (e) {
                val = raw;
              }
            } else {
              val = Array.isArray(raw) ? raw[0] : String(raw);
            }
          }
          incrementOption(entry.optionsMap, val);
        }
      });
    });

    const stats = Array.from(questionsMap.values()).map((entry) => {
      const optionsArray = Array.from(entry.optionsMap.entries()).map(([label, count]) => ({
        label,
        count
      }));
      optionsArray.sort((a, b) => b.count - a.count);
      const totalForOptions = optionsArray.reduce((sum, o) => sum + o.count, 0) || 0;
      return {
        id: entry.id,
        text: entry.text,
        type: entry.type,
        is_deleted: entry.is_deleted,
        totalAnswers: entry.totalAnswers,
        options: optionsArray,
        totalForOptions
      };
    });

    // Порядок вопросов оставляем таким, как он встретился впервые
    // (он соответствует порядку вопросов в первой заявке/пресете)
    return stats;
  }, [submissions]);

  return (
    <div className="form-modal-overlay">
      <div className="form-modal submissions-modal" onClick={(e) => e.stopPropagation()}>
        <div className="form-modal-header">
          <div className="header-content">
            <h3>Ответы ({submissions.length})</h3>
            {submissions.length > 0 && (
              <button 
                type="button" 
                className="btn btn-secondary btn-export"
                onClick={handleExportToExcel}
                title="Экспортировать в Excel"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                <span className="btn-export-text">Экспорт в Excel</span>
              </button>
            )}
          </div>
          {submissions.length > 0 && (
            <div className="submissions-view-toggle">
              <button
                type="button"
                className={viewMode === 'byUsers' ? 'active' : ''}
                onClick={() => setViewMode('byUsers')}
              >
                По заявкам
              </button>
              <button
                type="button"
                className={viewMode === 'byQuestions' ? 'active' : ''}
                onClick={() => setViewMode('byQuestions')}
              >
                По вопросам
              </button>
            </div>
          )}
          <button type="button" className="btn-close-form" onClick={onClose} title="Закрыть">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="form-modal-body submissions-body">
          {submissions.length === 0 ? (
            <div className="empty-state">
              <p>Нет отправленных заявок</p>
            </div>
          ) : (
            <>
              {isMobile && submissions.length > 0 && viewMode === 'byUsers' && (
                <p className="submissions-mobile-hint">Подробные ответы — в выгрузке Excel или на версии для ПК</p>
              )}
              {viewMode === 'byUsers' ? (
            <div
              ref={tableContainerRef}
              className="submissions-table-container"
              style={selectedSubmission && tableWidth != null ? { width: tableWidth, minWidth: tableWidth } : undefined}
            >
              <table
                className="submissions-table"
                style={selectedSubmission && tableWidth != null ? { width: tableWidth, minWidth: tableWidth } : undefined}
              >
                <thead>
                  <tr>
                    <th>Никнейм</th>
                    <th>Discord ID</th>
                    <th>Дата отправки</th>
                    <th>Статус</th>
                    <th className="actions-column">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map(submission => (
                    <React.Fragment key={submission.id}>
                      <tr
                        className={!isMobile && selectedSubmission?.id === submission.id ? 'active' : ''}
                        onClick={isMobile ? undefined : () => setSelectedSubmission(selectedSubmission?.id === submission.id ? null : submission)}
                      >
                        <td>
                          <strong>{submission.nickname_from_answer || submission.discord_tag || submission.submitted_by_discord_tag || submission.submitted_by_username || submission.discord_username || 'Не указан'}</strong>
                        </td>
                        <td>
                          <code style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            {submission.discord_id_from_answer || submission.discord_id || submission.submitted_by_discord_id || 'Не указан'}
                          </code>
                        </td>
                        <td>{formatDate(submission.created_at)}</td>
                        <td>
                          <select
                            value={submission.status}
                            onChange={(e) => {
                              e.stopPropagation();
                              onUpdateStatus(submission.id, e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className={`submissions-status-select ${getStatusBadgeClass(submission.status)}`}
                          >
                            <option value="pending">Ожидает</option>
                            <option value="reviewed">Рассмотрена</option>
                            <option value="accepted">Принята</option>
                            <option value="rejected">Отклонена</option>
                          </select>
                        </td>
                        <td className="actions-column">
                          {!isMobile && (
                          <button
                            type="button"
                            className="btn-icon btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSubmission(selectedSubmission?.id === submission.id ? null : submission);
                            }}
                            title={selectedSubmission?.id === submission.id ? 'Скрыть детали' : 'Показать детали'}
                          >
                            <svg 
                              width="16" 
                              height="16" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2"
                              style={{ transform: selectedSubmission?.id === submission.id ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                            >
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </button>
                          )}
                        </td>
                      </tr>
                      {!isMobile && selectedSubmission?.id === submission.id && (
                        <tr className="submission-details-row">
                          <td colSpan="5">
                            <div className="submission-details">
                              <div className="submission-answers">
                                {submission.questions_with_answers && submission.questions_with_answers.length > 0 ? (
                                  submission.questions_with_answers.map((question, index) => (
                                    <div 
                                      key={question.question_id || index} 
                                      className={`answer-item ${question.is_deleted ? 'answer-item-deleted' : ''} ${question.was_added_after ? 'answer-item-added-after' : ''}`}
                                    >
                                      <div className="answer-question">
                                        <strong>{question.question_text}</strong>
                                        {question.is_deleted && (
                                          <span className="answer-status-badge answer-status-deleted" title="Этот вопрос был удален после заполнения заявки">
                                            Удален
                                          </span>
                                        )}
                                        {question.was_added_after && !question.has_answer && (
                                          <span className="answer-status-badge answer-status-added-after" title="Этот вопрос был добавлен после заполнения заявки">
                                            Добавлен позже
                                          </span>
                                        )}
                                        {question.question_type && (
                                          <span className="answer-type-badge">
                                            {question.question_type === 'text' && 'Текст'}
                                            {question.question_type === 'textarea' && 'Многострочный текст'}
                                            {question.question_type === 'select' && 'Выпадающий список'}
                                            {question.question_type === 'radio' && 'Один вариант'}
                                            {question.question_type === 'checkbox' && 'Несколько вариантов'}
                                            {question.question_type === 'server' && 'Выбор сервера'}
                                            {question.question_type === 'scale' && 'Шкала'}
                                          </span>
                                        )}
                                      </div>
                                      <div className="answer-value">
                                        {question.has_answer ? (
                                          formatAnswer({
                                            ...question.answer,
                                            question_type: question.question_type
                                          })
                                        ) : (
                                          <span className="answer-no-value">—</span>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                ) : submission.answers && submission.answers.length > 0 ? (
                                  // Fallback на старый формат для обратной совместимости
                                  submission.answers.map((answer, index) => (
                                    <div key={index} className="answer-item">
                                      <div className="answer-question">
                                        <strong>{answer.question_text}</strong>
                                        {answer.question_type && (
                                          <span className="answer-type-badge">
                                            {answer.question_type === 'text' && 'Текст'}
                                            {answer.question_type === 'textarea' && 'Многострочный текст'}
                                            {answer.question_type === 'select' && 'Выпадающий список'}
                                            {answer.question_type === 'radio' && 'Один вариант'}
                                            {answer.question_type === 'checkbox' && 'Несколько вариантов'}
                                            {answer.question_type === 'server' && 'Выбор сервера'}
                                            {answer.question_type === 'scale' && 'Шкала'}
                                          </span>
                                        )}
                                      </div>
                                      <div className="answer-value">
                                        {formatAnswer(answer)}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="answer-empty">Нет ответов</div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
              ) : (
                <div className="submissions-questions-view">
                  {questionsStats.length === 0 ? (
                    <p className="submissions-questions-empty">Нет данных по вопросам</p>
                  ) : (
                    questionsStats.map((q) => {
                      const hasPie = q.totalForOptions > 0 && q.type && q.type !== 'text' && q.type !== 'textarea';
                      const totalSubmissions = submissions.length;
                      const palette = ['#ff8000', '#ff6a00', '#ec4899', '#f59e0b', '#10b981', '#ff6a00', '#ffb84d', '#f97316'];
                      let current = 0;
                      const segments = hasPie ? q.options.map((opt, index) => {
                        const percent = q.totalForOptions ? (opt.count / q.totalForOptions) * 100 : 0;
                        const start = current;
                        const end = start + percent;
                        current = end;
                        return { ...opt, percent, start, end, color: palette[index % palette.length] };
                      }) : [];
                      const gradient = hasPie && segments.length > 0 ? segments.map(s => `${s.color} ${s.start}% ${s.end}%`).join(', ') : 'var(--bg-secondary) 0 100%';
                      const answerList = submissions.map((sub) => {
                        const list = sub.questions_with_answers || sub.answers || [];
                        const qa = list.find((x) => (x.question_id || x.id) === q.id);
                        if (!qa) return null;
                        const raw = qa.answer || qa;
                        const text = getAnswerText({ ...raw, question_type: qa.question_type || q.type });
                        if (text === '—' && !qa.has_answer) return null;
                        const nickname = sub.nickname_from_answer || sub.discord_tag || sub.submitted_by_discord_tag || sub.submitted_by_username || sub.discord_username || 'Аноним';
                        return { nickname, text };
                      }).filter(Boolean);
                      return (
                        <div key={q.id} className="question-stats-card">
                          <div className="question-stats-header">
                            <div className="question-stats-title">
                              <strong>{q.text}</strong>
                              {q.is_deleted && <span className="answer-status-badge answer-status-deleted">Удален</span>}
                            </div>
                            <div className="question-stats-badges">
                              {q.type && (
                                <span className="answer-type-badge">
                                  {q.type === 'text' && 'Текст'}
                                  {q.type === 'textarea' && 'Многострочный текст'}
                                  {q.type === 'select' && 'Выпадающий список'}
                                  {q.type === 'radio' && 'Один вариант'}
                                  {q.type === 'checkbox' && 'Несколько вариантов'}
                                  {q.type === 'server' && 'Выбор сервера'}
                                  {q.type === 'scale' && 'Шкала'}
                                </span>
                              )}
                              <span className="question-stats-meta">Ответов: {q.totalAnswers} из {totalSubmissions}</span>
                            </div>
                          </div>
                          {hasPie && (
                            <div className="question-stats-chart-block">
                              <div className="question-pie-wrap">
                                <div className="question-pie" style={{ background: `conic-gradient(${gradient})` }}>
                                  <div className="question-pie-center">
                                    <span className="question-pie-total">{q.totalForOptions}</span>
                                    <span className="question-pie-label">ответов</span>
                                  </div>
                                </div>
                              </div>
                              <div className="question-legend">
                                {segments.map((s) => (
                                  <div key={s.label} className="question-legend-item">
                                    <span className="question-legend-color" style={{ backgroundColor: s.color }} />
                                    <span className="question-legend-label">{s.label}</span>
                                    <span className="question-legend-value">{s.count} ({Math.round(s.percent)}%)</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="question-answers-block">
                            <div className="question-answers-title">Ответы пользователей</div>
                            {answerList.length === 0 ? (
                              <p className="question-answers-empty">Нет ответов</p>
                            ) : (
                              <ul className="question-answers-list">
                                {answerList.map((item, idx) => (
                                  <li key={idx} className="question-answer-item">
                                    <span className="question-answer-nick">{item.nickname}</span>
                                    <span className="question-answer-text">{item.text}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Компонент модального окна управления пресетами
const PresetsModal = ({ presets, onClose, onRefresh }) => {
  const [localPresets, setLocalPresets] = React.useState(presets);
  const [showForm, setShowForm] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = React.useState({});
  const [scaleMinSelectOpen, setScaleMinSelectOpen] = React.useState({});
  const [scaleMaxSelectOpen, setScaleMaxSelectOpen] = React.useState({});
  const scaleMinSelectRefs = React.useRef({});
  const scaleMaxSelectRefs = React.useRef({});
  const [questionTypeSelectOpen, setQuestionTypeSelectOpen] = React.useState({});
  const questionTypeSelectRefs = React.useRef({});
  const [draggedQuestionIndex, setDraggedQuestionIndex] = React.useState(null);
  const [dragOverIndex, setDragOverIndex] = React.useState(null);
  const [isPresentationExpanded, setIsPresentationExpanded] = React.useState(false);
  const [confirmModal, setConfirmModal] = React.useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    intro_text: '',
    completion_text: '',
    questions: []
  });
  const [initialFormData, setInitialFormData] = React.useState(null);
  const [showUnsavedModal, setShowUnsavedModal] = React.useState(false);

  React.useEffect(() => {
    setLocalPresets(presets);
  }, [presets]);

  // Сохраняем начальное состояние формы при открытии
  React.useEffect(() => {
    if (showForm) {
      setInitialFormData(JSON.parse(JSON.stringify(formData)));
    }
  }, [showForm]);

  // При загрузке пресета для редактирования раскрывать блок «Описание» у вопросов, у которых оно задано
  const presetQuestionsDescriptionFlags = (formData.questions || []).map(q => !!(q.question_description && String(q.question_description).trim()));
  React.useEffect(() => {
    if (!formData.questions || !formData.questions.length) return;
    setExpandedDescriptions(prev => {
      const next = { ...prev };
      formData.questions.forEach((q, i) => {
        if (q.question_description != null && String(q.question_description).trim()) next[i] = true;
      });
      return next;
    });
  }, [presetQuestionsDescriptionFlags.join('')]);

  // Функция проверки изменений
  const hasUnsavedChanges = () => {
    if (!initialFormData || !showForm) return false;
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  };

  // Обработчик закрытия с проверкой изменений
  const handleCloseForm = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedModal(true);
    } else {
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', description: '', intro_text: '', completion_text: '', questions: [] });
    }
  };

  // Сохранить и закрыть
  const handleSaveAndClose = async () => {
    setShowUnsavedModal(false);
    try {
      const form = document.getElementById('preset-form');
      if (form) {
        const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
        form.dispatchEvent(submitEvent);
        // Ждем завершения сохранения
        await new Promise(resolve => setTimeout(resolve, 500));
        // Форма закроется автоматически после успешного сохранения в handleSubmit
      }
    } catch (err) {
      console.error('Error saving:', err);
    }
  };

  // Закрыть без сохранения
  const handleCloseWithoutSave = () => {
    setShowUnsavedModal(false);
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', description: '', intro_text: '', completion_text: '', questions: [] });
  };

  // Закрытие выпадающих списков при клике вне их
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      Object.keys(questionTypeSelectOpen).forEach(index => {
        if (questionTypeSelectOpen[index]) {
          const ref = questionTypeSelectRefs.current[index];
          if (ref && !ref.contains(e.target)) {
            setQuestionTypeSelectOpen(prev => ({ ...prev, [index]: false }));
          }
        }
      });
      
      Object.keys(scaleMinSelectOpen).forEach(index => {
        if (scaleMinSelectOpen[index]) {
          const ref = scaleMinSelectRefs.current[index];
          if (ref && !ref.contains(e.target)) {
            setScaleMinSelectOpen(prev => ({ ...prev, [index]: false }));
          }
        }
      });
      
      Object.keys(scaleMaxSelectOpen).forEach(index => {
        if (scaleMaxSelectOpen[index]) {
          const ref = scaleMaxSelectRefs.current[index];
          if (ref && !ref.contains(e.target)) {
            setScaleMaxSelectOpen(prev => ({ ...prev, [index]: false }));
          }
        }
      });
    };
    
    const hasOpen = Object.values(questionTypeSelectOpen).some(open => open) ||
                    Object.values(scaleMinSelectOpen).some(open => open) ||
                    Object.values(scaleMaxSelectOpen).some(open => open);
    if (hasOpen) {
      document.addEventListener('click', handleClickOutside, true);
      return () => document.removeEventListener('click', handleClickOutside, true);
    }
  }, [questionTypeSelectOpen, scaleMinSelectOpen, scaleMaxSelectOpen]);

  const handleAddClick = () => {
    setEditingId(null);
    setFormData({ name: '', description: '', intro_text: '', completion_text: '', questions: [] });
    setShowForm(true);
  };

  const handleEditClick = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/application-presets/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const preset = await response.json();
        setEditingId(id);
        // Фильтруем системные вопросы при загрузке пресета
        const filteredQuestions = preset.questions
          .filter(q => q.question_text !== 'Discord ID' && q.question_text !== 'Никнейм')
          .map(q => ({
            ...q,
            id: q.id || `temp-${Date.now()}-${Math.random()}`, // Убеждаемся, что у каждого вопроса есть ID
            question_description: q.question_description || '',
            options: q.options ? (typeof q.options === 'string' ? JSON.parse(q.options) : q.options) : null,
            has_other_option: q.has_other_option || false,
            is_required: q.is_required !== undefined ? q.is_required : true
          }));
        
        const newFormData = {
          name: preset.name,
          description: preset.description || '',
          intro_text: preset.intro_text || '',
          completion_text: preset.completion_text || '',
          questions: filteredQuestions
        };
        setFormData(newFormData);
        setInitialFormData(JSON.parse(JSON.stringify(newFormData)));
        setShowForm(true);
      }
    } catch (err) {
      console.error('Error loading preset:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот пресет?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/application-presets/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error deleting preset:', err);
    }
  };

  // Экспорт одного пресета в JSON-файл (из карточки)
  const [exportingId, setExportingId] = React.useState(null);
  const handleExportOnePreset = async (presetId) => {
    setExportingId(presetId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/application-presets/${presetId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        alert('Не удалось загрузить пресет');
        return;
      }
      const preset = await res.json();
      const questions = (preset.questions || []).map(q => ({
        question_text: q.question_text,
        question_description: q.question_description || '',
        question_type: q.question_type || 'text',
        options: q.options ? (typeof q.options === 'string' ? JSON.parse(q.options) : q.options) : null,
        is_required: q.is_required !== false
      }));
      const exportData = {
        name: preset.name,
        description: preset.description || '',
        intro_text: preset.intro_text || '',
        completion_text: preset.completion_text || '',
        questions
      };
      const json = JSON.stringify(exportData, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (preset.name || 'Пресет').replace(/[/\\:*?"<>|]/g, '-').trim() || 'Пресет';
      const dateStr = new Date().toLocaleDateString('ru-RU', { timeZone: 'Europe/Moscow' }).replace(/\./g, '-');
      a.download = `${safeName}_${dateStr}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert('Ошибка при экспорте пресета');
    } finally {
      setExportingId(null);
    }
  };

  // Импорт пресетов из JSON-файла (с системным диалогом переименования при дубликатах)
  const [importing, setImporting] = React.useState(false);
  const [importNotification, setImportNotification] = React.useState(null);
  const [renameModal, setRenameModal] = React.useState(null);
  const [renameInputValue, setRenameInputValue] = React.useState('');
  const importInputRef = React.useRef(null);
  const importContextRef = React.useRef(null);
  const processNextRef = React.useRef(null);

  const processImportNext = React.useCallback(async () => {
    const ctx = importContextRef.current;
    if (!ctx || ctx.index >= ctx.list.length) {
      if (ctx) {
        onRefresh();
        const msg = ctx.skipped > 0
          ? `Пресеты импортированы: ${ctx.created} из ${ctx.list.length} (${ctx.skipped} пропущено — дубликаты названий)`
          : `Пресеты импортированы: ${ctx.created} из ${ctx.list.length}`;
        setImportNotification({ message: msg, type: 'success' });
      }
      setImporting(false);
      importContextRef.current = null;
      return;
    }
    const preset = ctx.list[ctx.index];
    let name = preset.name?.trim?.() || preset.name;
    if (!name) {
      ctx.index++;
      processNextRef.current?.();
      return;
    }
    if (ctx.existingNames.has(name.toLowerCase())) {
      setRenameInputValue(name);
      setRenameModal({ preset, defaultName: name });
      return;
    }
    const questions = (preset.questions || []).map(q => ({
      question_text: q.question_text || '',
      question_description: q.question_description || '',
      question_type: q.question_type || 'text',
      options: q.options != null ? (typeof q.options === 'string' ? (() => { try { return JSON.parse(q.options); } catch { return q.options; } })() : q.options) : null,
      is_required: q.is_required !== false
    }));
    const payload = {
      name,
      description: preset.description ?? '',
      intro_text: preset.intro_text ?? '',
      completion_text: preset.completion_text ?? '',
      questions
    };
    try {
      const res = await fetch('/api/application-presets', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${ctx.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        ctx.created++;
        ctx.existingNames.add(name.toLowerCase());
      }
    } catch (e) {
      console.error(e);
    }
    ctx.index++;
    processNextRef.current?.();
  }, [onRefresh]);

  React.useEffect(() => {
    processNextRef.current = processImportNext;
  }, [processImportNext]);

  const handleImportPresets = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportNotification(null);
    setRenameModal(null);
    e.target.value = '';
    try {
      const text = await file.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        alert('Неверный формат файла. Ожидается JSON.');
        setImporting(false);
        return;
      }
      const list = Array.isArray(data) ? data : (data.presets ? data.presets : [data]);
      if (list.length === 0) {
        alert('В файле нет пресетов для импорта');
        setImporting(false);
        return;
      }
      const token = localStorage.getItem('token');
      importContextRef.current = {
        list,
        index: 0,
        existingNames: new Set(localPresets.map(p => (p.name || '').trim().toLowerCase())),
        created: 0,
        skipped: 0,
        token
      };
      processImportNext();
    } catch (err) {
      console.error('Import error:', err);
      alert('Ошибка при импорте пресетов');
      setImporting(false);
    }
  };

  const handleRenameModalConfirm = async () => {
    const chosen = renameInputValue.trim();
    if (!chosen) {
      const ctx = importContextRef.current;
      if (ctx) {
        ctx.skipped++;
        ctx.index++;
      }
      setRenameModal(null);
      processNextRef.current?.();
      return;
    }
    const ctx = importContextRef.current;
    if (ctx && ctx.existingNames.has(chosen.toLowerCase())) {
      alert('Пресет с таким названием уже есть. Введите другое или нажмите «Пропустить».');
      return;
    }
    const { preset } = renameModal || {};
    if (!preset || !ctx) {
      setRenameModal(null);
      processNextRef.current?.();
      return;
    }
    const questions = (preset.questions || []).map(q => ({
      question_text: q.question_text || '',
      question_description: q.question_description || '',
      question_type: q.question_type || 'text',
      options: q.options != null ? (typeof q.options === 'string' ? (() => { try { return JSON.parse(q.options); } catch { return q.options; } })() : q.options) : null,
      is_required: q.is_required !== false
    }));
    const payload = {
      name: chosen,
      description: preset.description ?? '',
      intro_text: preset.intro_text ?? '',
      completion_text: preset.completion_text ?? '',
      questions
    };
    try {
      const res = await fetch('/api/application-presets', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${ctx.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        ctx.created++;
        ctx.existingNames.add(chosen.toLowerCase());
      }
    } catch (e) {
      console.error(e);
    }
    ctx.index++;
    setRenameModal(null);
    processNextRef.current?.();
  };

  const handleRenameModalSkip = () => {
    const ctx = importContextRef.current;
    if (ctx) {
      ctx.skipped++;
      ctx.index++;
    }
    setRenameModal(null);
    processNextRef.current?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Валидация
    if (!formData.name || formData.name.trim() === '') {
      alert('Пожалуйста, укажите название пресета');
      return;
    }
    const nameNorm = formData.name.trim().toLowerCase();
    const duplicate = localPresets.some(
      p => p.name && p.name.trim().toLowerCase() === nameNorm && String(p.id) !== String(editingId)
    );
    if (duplicate) {
      alert('Пресет с таким названием уже существует');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const url = editingId 
        ? `/api/application-presets/${editingId}`
        : '/api/application-presets';
      const method = editingId ? 'PUT' : 'POST';
      
      // Фильтруем системные вопросы (Discord ID и Никнейм) - они не должны быть в пресетах
      const filteredQuestions = formData.questions.filter(
        q => q.question_text !== 'Discord ID' && q.question_text !== 'Никнейм'
      );
      
      // Подготавливаем данные для отправки
      const payload = {
        name: formData.name.trim(),
        description: formData.description ? formData.description.trim() : '',
        intro_text: formData.intro_text || '',
        completion_text: formData.completion_text || '',
        questions: filteredQuestions.map(q => ({
          question_text: q.question_text,
          question_description: q.question_description || '',
          question_type: q.question_type,
          options: q.options ? (typeof q.options === 'string' ? q.options : JSON.stringify(q.options)) : null,
          is_required: q.is_required !== undefined ? q.is_required : true,
          has_other_option: q.has_other_option || false
        }))
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Ошибка при сохранении пресета' }));
        throw new Error(errorData.error || 'Ошибка при сохранении пресета');
      }

      const result = await response.json();
      onRefresh();
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', description: '', intro_text: '', completion_text: '', questions: [] });
    } catch (err) {
      console.error('Error saving preset:', err);
      alert(err.message || 'Ошибка при сохранении пресета');
    }
  };

  // Используем те же функции для работы с вопросами, что и в ApplicationFormModal
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddQuestion = (afterIndex = null) => {
    setFormData(prev => {
      const newQuestion = {
        id: `temp-${Date.now()}-${Math.random()}`, // Временный уникальный ID
        question_text: '',
        question_description: '',
        question_type: 'text',
        options: null,
        is_required: true,
        has_other_option: false
      };
      
      if (afterIndex === null || afterIndex === undefined) {
        // Добавляем в конец
        return {
          ...prev,
          questions: [...prev.questions, newQuestion]
        };
      } else {
        // Добавляем после указанного индекса
        const newQuestions = [...prev.questions];
        newQuestions.splice(afterIndex + 1, 0, newQuestion);
        return { ...prev, questions: newQuestions };
      }
    });
  };

  const handleRemoveQuestion = (index) => {
    const question = formData.questions[index];
    const questionText = question?.question_text || 'вопрос';
    
    setConfirmModal({
      isOpen: true,
      title: 'Удалить вопрос?',
      message: `Вы уверены, что хотите удалить вопрос "${questionText}"? Это действие нельзя отменить.`,
      onConfirm: () => {
        setFormData(prev => ({
          ...prev,
          questions: prev.questions.filter((_, i) => i !== index)
        }));
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  const handleQuestionChange = (index, field, value) => {
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[index] = { ...newQuestions[index], [field]: value };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleQuestionTypeChange = (index, newType) => {
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[index] = {
        ...newQuestions[index],
        question_type: newType,
        options: (newType === 'select' || newType === 'radio' || newType === 'checkbox') 
          ? (newQuestions[index].options || ['Вариант 1', 'Вариант 2'])
          : (newType === 'scale')
          ? (newQuestions[index].options || { min: 1, max: 5, label_min: '', label_max: '' })
          : null,
        has_other_option: (newType === 'select' || newType === 'radio' || newType === 'checkbox') 
          ? (newQuestions[index].has_other_option || false)
          : false
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleAddOption = (index, optionIndex = null) => {
    const question = formData.questions[index];
    if (!question) return;
    
    const currentOptions = question.options || [];
    const newOptions = [...currentOptions];
    
    if (optionIndex === null) {
      // Добавляем в конец
      newOptions.push('');
    } else {
      // Добавляем после указанного индекса
      newOptions.splice(optionIndex + 1, 0, '');
    }
    
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[index] = {
        ...newQuestions[index],
        options: newOptions
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleRemoveOption = (index, optionIndex) => {
    const question = formData.questions[index];
    if (!question || !question.options || question.options.length <= 1) return;
    
    const newOptions = question.options.filter((_, i) => i !== optionIndex);
    
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[index] = {
        ...newQuestions[index],
        options: newOptions
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleOptionTextChange = (index, optionIndex, value) => {
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      const newOptions = [...(newQuestions[index].options || [])];
      newOptions[optionIndex] = value;
      newQuestions[index] = {
        ...newQuestions[index],
        options: newOptions
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleToggleOtherOption = (index) => {
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      newQuestions[index] = {
        ...newQuestions[index],
        has_other_option: !newQuestions[index].has_other_option
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleDragStart = (e, index) => {
    setDraggedQuestionIndex(index);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', e.target.outerHTML);
    }
    const questionItem = e.currentTarget.closest('.question-item');
    if (questionItem) {
      questionItem.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e) => {
    const questionItem = e.currentTarget.closest('.question-item');
    if (questionItem) {
      questionItem.style.opacity = '1';
    }
    setDraggedQuestionIndex(null);
    setDragOverIndex(null);
  };

  // Touch handlers для мобильных устройств в PresetsModal
  const touchStartRefPreset = React.useRef({ 
    x: 0, 
    y: 0, 
    index: null, 
    element: null,
    startTime: null,
    holdTimer: null,
    isDragging: false,
    scrollInterval: null
  });

  const handleTouchStartPreset = (e, index) => {
    // Не начинаем перетаскивание, если клик был на кнопке
    const target = e.target;
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      return;
    }

    const touch = e.touches[0];
    const questionItem = e.currentTarget.closest('.question-item');
    
    // Очищаем предыдущий таймер, если есть
    if (touchStartRefPreset.current.holdTimer) {
      clearTimeout(touchStartRefPreset.current.holdTimer);
    }
    
    touchStartRefPreset.current = {
      x: touch.clientX,
      y: touch.clientY,
      index: index,
      element: questionItem,
      startTime: Date.now(),
      holdTimer: null,
      isDragging: false,
      scrollInterval: null
    };
    
    // Запускаем таймер на 1 секунду
    touchStartRefPreset.current.holdTimer = setTimeout(() => {
      // Вибрация при активации
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      touchStartRefPreset.current.isDragging = true;
      
      if (questionItem) {
        questionItem.style.opacity = '0.5';
        questionItem.style.transform = 'scale(1.05)';
        questionItem.style.transition = 'transform 0.2s';
      }
      
      setDraggedQuestionIndex(index);
    }, 1000);
  };

  const handleTouchMovePreset = (e) => {
    if (!touchStartRefPreset.current.element || !touchStartRefPreset.current.isDragging) {
      // Если еще не прошло 1 секунду, отменяем таймер при движении
      const deltaX = Math.abs(e.touches[0].clientX - touchStartRefPreset.current.x);
      const deltaY = Math.abs(e.touches[0].clientY - touchStartRefPreset.current.y);
      if (deltaX > 10 || deltaY > 10) {
        if (touchStartRefPreset.current.holdTimer) {
          clearTimeout(touchStartRefPreset.current.holdTimer);
          touchStartRefPreset.current.holdTimer = null;
        }
      }
      return;
    }
    
    const touch = e.touches[0];
    e.preventDefault(); // Предотвращаем скролл только после активации
    
    // Автоматическая прокрутка при приближении к краям
    const scrollThreshold = 100;
    const scrollSpeed = 10;
    
    const container = document.querySelector('.preset-form-modal .form-modal-body');
    if (container) {
      const containerRect = container.getBoundingClientRect();
      const touchY = touch.clientY;
      
      // Останавливаем предыдущую прокрутку
      if (touchStartRefPreset.current.scrollInterval) {
        clearInterval(touchStartRefPreset.current.scrollInterval);
        touchStartRefPreset.current.scrollInterval = null;
      }
      
      // Прокрутка вверх
      if (touchY < containerRect.top + scrollThreshold) {
        touchStartRefPreset.current.scrollInterval = setInterval(() => {
          if (container.scrollTop > 0) {
            container.scrollTop -= scrollSpeed;
          } else {
            if (touchStartRefPreset.current.scrollInterval) {
              clearInterval(touchStartRefPreset.current.scrollInterval);
              touchStartRefPreset.current.scrollInterval = null;
            }
          }
        }, 16);
      }
      // Прокрутка вниз
      else if (touchY > containerRect.bottom - scrollThreshold) {
        touchStartRefPreset.current.scrollInterval = setInterval(() => {
          if (container.scrollTop < container.scrollHeight - container.clientHeight) {
            container.scrollTop += scrollSpeed;
          } else {
            if (touchStartRefPreset.current.scrollInterval) {
              clearInterval(touchStartRefPreset.current.scrollInterval);
              touchStartRefPreset.current.scrollInterval = null;
            }
          }
        }, 16);
      }
    }
    
    // Находим элемент под пальцем
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
    const questionItemBelow = elementBelow?.closest('.question-item');
    
    // Убираем подсветку со всех элементов
    document.querySelectorAll('.preset-form-modal .questions-list .question-item').forEach(item => {
      if (item !== touchStartRefPreset.current.element) {
        item.style.border = '';
        item.style.boxShadow = '';
      }
    });
    
    if (questionItemBelow && questionItemBelow !== touchStartRefPreset.current.element) {
      // Визуальная индикация элемента, на который ведется
      questionItemBelow.style.border = '2px solid var(--accent-color)';
      questionItemBelow.style.boxShadow = '0 0 10px rgba(255, 128, 0, 0.5)';
      
      // Находим индекс элемента под пальцем
      const allItems = document.querySelectorAll('.preset-form-modal .questions-list .question-item');
      let newIndex = -1;
      allItems.forEach((item, idx) => {
        if (item === questionItemBelow) {
          newIndex = idx;
        }
      });
      
      if (newIndex >= 0 && newIndex !== touchStartRefPreset.current.index) {
        setDragOverIndex(newIndex);
      }
    }
  };

  const handleTouchEndPreset = (e) => {
    // Очищаем таймер, если перетаскивание не началось
    if (touchStartRefPreset.current.holdTimer) {
      clearTimeout(touchStartRefPreset.current.holdTimer);
      touchStartRefPreset.current.holdTimer = null;
    }
    
    // Останавливаем прокрутку
    if (touchStartRefPreset.current.scrollInterval) {
      clearInterval(touchStartRefPreset.current.scrollInterval);
      touchStartRefPreset.current.scrollInterval = null;
    }
    
    if (!touchStartRefPreset.current.element || !touchStartRefPreset.current.isDragging) {
      // Если перетаскивание не было активировано, просто сбрасываем состояние
      touchStartRefPreset.current = { 
        x: 0, 
        y: 0, 
        index: null, 
        element: null,
        startTime: null,
        holdTimer: null,
        isDragging: false,
        scrollInterval: null
      };
      return;
    }
    
    const touch = e.changedTouches[0];
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
    const questionItemBelow = elementBelow?.closest('.question-item');
    
    // Убираем визуальные эффекты со всех элементов
    document.querySelectorAll('.preset-form-modal .questions-list .question-item').forEach(item => {
      item.style.opacity = '1';
      item.style.transform = '';
      item.style.border = '';
      item.style.boxShadow = '';
    });
    
    if (questionItemBelow && questionItemBelow !== touchStartRefPreset.current.element) {
      // Находим индекс элемента под пальцем
      const allItems = document.querySelectorAll('.preset-form-modal .questions-list .question-item');
      let dropIndex = -1;
      allItems.forEach((item, idx) => {
        if (item === questionItemBelow) {
          dropIndex = idx;
        }
      });
      
      if (dropIndex >= 0 && touchStartRefPreset.current.index !== null) {
        const draggedIndex = touchStartRefPreset.current.index;
        
        if (draggedIndex !== dropIndex) {
          // Перемещаем вопрос
          setFormData(prev => {
            const newQuestions = [...prev.questions];
            const draggedQuestion = newQuestions[draggedIndex];
            newQuestions.splice(draggedIndex, 1);
            newQuestions.splice(dropIndex, 0, draggedQuestion);
            return { ...prev, questions: newQuestions };
          });
        }
      }
    }
    
    // Сбрасываем состояние
    touchStartRefPreset.current = { 
      x: 0, 
      y: 0, 
      index: null, 
      element: null,
      startTime: null,
      holdTimer: null,
      isDragging: false,
      scrollInterval: null
    };
    setDraggedQuestionIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedQuestionIndex !== null && draggedQuestionIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedQuestionIndex === null || draggedQuestionIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    setFormData(prev => {
      const newQuestions = [...prev.questions];
      const draggedQuestion = newQuestions[draggedQuestionIndex];
      newQuestions.splice(draggedQuestionIndex, 1);
      newQuestions.splice(dropIndex, 0, draggedQuestion);
      return { ...prev, questions: newQuestions };
    });

    setDraggedQuestionIndex(null);
    setDragOverIndex(null);
  };

  // Глобальные обработчики touch для мобильных устройств в PresetsModal
  React.useEffect(() => {
    const handleGlobalTouchMove = (e) => {
      if (touchStartRefPreset.current.element) {
        handleTouchMovePreset(e);
      }
    };

    const handleGlobalTouchEnd = (e) => {
      if (touchStartRefPreset.current.element) {
        handleTouchEndPreset(e);
      }
    };

    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    document.addEventListener('touchend', handleGlobalTouchEnd);

    return () => {
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [formData.questions]);

  if (showForm) {
    return (
      <>
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
        />
        <div className="form-modal-overlay">
          <div className="form-modal preset-form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="form-modal-header">
              <div className="header-content">
                <h3>{editingId ? 'Редактировать пресет' : 'Создать пресет'}</h3>
              </div>
              <div className="header-actions">
                <button 
                  type="submit" 
                  form="preset-form"
                  className="btn btn-primary" 
                  style={{ marginRight: '8px' }}
                >
                  {editingId ? 'Сохранить' : 'Создать'}
                </button>
                <button type="button" className="btn-close-form" onClick={handleCloseForm}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>
            <UnsavedChangesModal
              isOpen={showUnsavedModal}
              onClose={() => setShowUnsavedModal(false)}
              onSaveAndClose={handleSaveAndClose}
              onCloseWithoutSave={handleCloseWithoutSave}
            />
          <form id="preset-form" onSubmit={handleSubmit} className="form-modal-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'start' }}>
              <div className="form-group">
                <label htmlFor="preset-name">Название пресета *</label>
                <input
                  type="text"
                  id="preset-name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="preset-description">Описание</label>
                <input
                  type="text"
                  id="preset-description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Описание пресета (необязательно)"
                />
              </div>
            </div>
            <div className="presentation-section">
              <div className="section-header" style={{ cursor: 'pointer' }} onClick={() => setIsPresentationExpanded(!isPresentationExpanded)}>
                <h4>Презентация</h4>
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  style={{ 
                    transform: isPresentationExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                    color: 'var(--text-muted)'
                  }}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              {isPresentationExpanded && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label htmlFor="preset-intro_text">Вступительное письмо</label>
                    <textarea
                      id="preset-intro_text"
                      name="intro_text"
                      value={formData.intro_text || ''}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Вступительное письмо"
                    />
                    <small style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Это письмо будет отображаться перед вопросами в форме заявки. Для форматирования используйте: <strong>**жирный текст**</strong> для жирного шрифта и <strong>__подчеркнутый текст__</strong> для подчеркивания.
                    </small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="preset-completion_text">Письмо завершения</label>
                    <textarea
                      id="preset-completion_text"
                      name="completion_text"
                      value={formData.completion_text || ''}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Письмо завершения"
                    />
                    <small style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Это письмо будет отображаться после успешной отправки заявки. Для форматирования используйте: <strong>**жирный текст**</strong> для жирного шрифта и <strong>__подчеркнутый текст__</strong> для подчеркивания.
                    </small>
                  </div>
                </div>
              )}
            </div>
            <div className="questions-section">
              <div className="section-header">
                <h4>Вопросы</h4>
              </div>
              <div style={{ 
                padding: '12px 16px', 
                marginBottom: '16px', 
                background: 'rgba(255, 128, 0, 0.1)', 
                border: '1px solid rgba(255, 128, 0, 0.2)', 
                borderRadius: '8px',
                fontSize: '13px',
                color: 'var(--text-secondary)',
                lineHeight: '1.5'
              }}>
                <strong style={{ color: 'var(--accent-color)', display: 'block', marginBottom: '4px' }}>
                  ℹ️ Информация о системных вопросах
                </strong>
                При создании заявки автоматически добавляются два системных вопроса: <strong>Discord ID</strong> и <strong>Никнейм</strong>. Эти вопросы не нужно добавлять в пресет, они будут присутствовать в любой заявке автоматически.
              </div>
              {formData.questions.length === 0 ? (
                <div className="empty-questions">
                  <button type="button" className="btn btn-secondary btn-add-question-empty" onClick={handleAddQuestion}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Добавить первый вопрос
                  </button>
                </div>
              ) : (
                <div className="questions-list">
                  {formData.questions.map((q, index) => (
                    <div 
                      key={q.id || `question-${index}`} 
                      className={`question-item ${draggedQuestionIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                    >
                      <div 
                        className="question-header"
                        draggable
                        onDragStart={(e) => {
                          const target = e.target;
                          if (target.tagName === 'BUTTON' || target.closest('button')) {
                            e.preventDefault();
                            return false;
                          }
                          handleDragStart(e, index);
                        }}
                        onDragEnd={handleDragEnd}
                        onTouchStart={(e) => handleTouchStartPreset(e, index)}
                        onTouchMove={handleTouchMovePreset}
                        onTouchEnd={handleTouchEndPreset}
                        style={{ cursor: 'grab', touchAction: 'none' }}
                      >
                        <span className="question-number">
                          <svg 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                            style={{ 
                              marginRight: '8px', 
                              display: 'inline-block',
                              verticalAlign: 'middle',
                              cursor: 'grab',
                              color: 'var(--text-muted)'
                            }}
                          >
                            <circle cx="9" cy="12" r="1"></circle>
                            <circle cx="9" cy="5" r="1"></circle>
                            <circle cx="9" cy="19" r="1"></circle>
                            <circle cx="15" cy="12" r="1"></circle>
                            <circle cx="15" cy="5" r="1"></circle>
                            <circle cx="15" cy="19" r="1"></circle>
                          </svg>
                          Вопрос {index + 1}
                        </span>
                        <div className="question-actions">
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => handleAddQuestion(index)}
                            title="Добавить вопрос после этого"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="12" y1="5" x2="12" y2="19"></line>
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="btn-icon btn-danger"
                            onClick={() => handleRemoveQuestion(index)}
                            title="Удалить вопрос"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div className="form-group question-main-fields">
                        <div className="question-text-field">
                          <label>Текст вопроса *</label>
                          <input
                            type="text"
                            value={q.question_text}
                            onChange={(e) => handleQuestionChange(index, 'question_text', e.target.value)}
                            required
                            placeholder="Введите вопрос"
                          />
                        </div>
                        <div className="question-type-field">
                          <label>Тип вопроса</label>
                          <div className={`question-type-select-wrap ${questionTypeSelectOpen[index] ? 'open' : ''}`} ref={el => questionTypeSelectRefs.current[index] = el}>
                            <div
                              className={`question-type-select-custom ${questionTypeSelectOpen[index] ? 'open' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const isCurrentlyOpen = questionTypeSelectOpen[index];
                                setQuestionTypeSelectOpen(prev => {
                                  if (isCurrentlyOpen) {
                                    return { ...prev, [index]: false };
                                  }
                                  return { [index]: true };
                                });
                              }}
                              role="combobox"
                              aria-expanded={questionTypeSelectOpen[index]}
                              aria-haspopup="listbox"
                            >
                              <span className="question-type-select-value">
                                {(() => {
                                  const typeLabels = {
                                    'text': 'Текст (одна строка)',
                                    'textarea': 'Текст (много строк)',
                                    'select': 'Выпадающий список',
                                    'radio': 'Один вариант',
                                    'checkbox': 'Несколько вариантов',
                                    'server': 'Выбор сервера',
                                    'scale': 'Шкала'
                                  };
                                  return typeLabels[q.question_type] || q.question_type;
                                })()}
                              </span>
                              <span className="question-type-select-arrow">▾</span>
                            </div>
                            {questionTypeSelectOpen[index] && (
                              <div className="question-type-select-dropdown" role="listbox">
                                {[
                                  { value: 'text', label: 'Текст (одна строка)' },
                                  { value: 'textarea', label: 'Текст (много строк)' },
                                  { value: 'select', label: 'Выпадающий список' },
                                  { value: 'radio', label: 'Один вариант' },
                                  { value: 'checkbox', label: 'Несколько вариантов' },
                                  { value: 'server', label: 'Выбор сервера' },
                                  { value: 'scale', label: 'Шкала' }
                                ].map((type) => (
                                  <div
                                    key={type.value}
                                    className={`question-type-select-option ${q.question_type === type.value ? 'selected' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleQuestionTypeChange(index, type.value);
                                      setQuestionTypeSelectOpen(prev => ({ ...prev, [index]: false }));
                                    }}
                                    role="option"
                                  >
                                    <span className="question-type-select-option-text">{type.label}</span>
                                    {q.question_type === type.value && <span className="question-type-select-option-check">✓</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {(q.question_type === 'select' || 
                        q.question_type === 'radio' || 
                        q.question_type === 'checkbox') && (
                        <div className="form-group">
                          <label>Варианты ответов *</label>
                          <div className="options-list" style={{ marginTop: '8px' }}>
                            {q.options && q.options.length > 0 ? (
                              q.options.map((option, optIndex) => (
                                <div key={optIndex} className="option-item" style={{ 
                                  display: 'flex', 
                                  gap: '8px', 
                                  alignItems: 'center', 
                                  marginBottom: '8px',
                                  padding: '8px',
                                  background: 'var(--bg-secondary)',
                                  borderRadius: '6px',
                                  border: '1px solid var(--border-color)'
                                }}>
                                  <input
                                    type="text"
                                    value={option}
                                    onChange={(e) => handleOptionTextChange(index, optIndex, e.target.value)}
                                    placeholder={`Вариант ${optIndex + 1}`}
                                    style={{ 
                                      flex: 1,
                                      padding: '8px 12px',
                                      background: 'var(--bg-primary)',
                                      border: '1px solid var(--border-color)',
                                      borderRadius: '4px',
                                      color: 'var(--text-primary)',
                                      fontSize: '14px'
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="btn-icon"
                                    onClick={() => handleAddOption(index, optIndex)}
                                    title="Добавить вариант после этого"
                                    style={{
                                      padding: '6px',
                                      background: 'transparent',
                                      border: 'none',
                                      color: 'var(--text-secondary)',
                                      cursor: 'pointer',
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <line x1="12" y1="5" x2="12" y2="19"></line>
                                      <line x1="5" y1="12" x2="19" y2="12"></line>
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-icon"
                                    onClick={() => handleRemoveOption(index, optIndex)}
                                    disabled={q.options.length <= 1}
                                    title="Удалить вариант"
                                    style={{
                                      padding: '6px',
                                      background: 'transparent',
                                      border: 'none',
                                      color: q.options.length <= 1 ? 'var(--text-disabled)' : 'var(--text-danger, #ef4444)',
                                      cursor: q.options.length <= 1 ? 'not-allowed' : 'pointer',
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <polyline points="3 6 5 6 21 6"></polyline>
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    </svg>
                                  </button>
                                </div>
                              ))
                            ) : (
                              <div style={{ color: 'var(--text-muted)', fontSize: '14px', padding: '12px', textAlign: 'center' }}>
                                Нет вариантов ответов
                              </div>
                            )}
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => handleAddOption(index)}
                              style={{
                                marginTop: '8px',
                                width: '100%',
                                padding: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                              </svg>
                              Добавить вариант
                            </button>
                          </div>
                          {q.question_type === 'radio' && (
                            <div className="form-group" style={{ marginTop: '12px' }}>
                              <label className="toggle-label">
                                <input
                                  type="checkbox"
                                  checked={q.has_other_option || false}
                                  onChange={() => handleToggleOtherOption(index)}
                                  className="toggle-input"
                                />
                                <span className="toggle-switch"></span>
                                <span className="toggle-text">Добавить "Другое"</span>
                              </label>
                            </div>
                          )}
                        </div>
                      )}

                      {q.question_type === 'server' && (
                        <div className="form-group">
                          <label>Выбор сервера</label>
                          <div className="server-select-wrap">
                            <div className="server-select-custom placeholder">
                              <span className="server-select-value">
                                <span className="server-select-placeholder">Список серверов загружается из настроек</span>
                              </span>
                              <span className="server-select-arrow">▾</span>
                            </div>
                          </div>
                          <small style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            При заполнении заявки пользователь сможет выбрать сервер из списка в настройках
                          </small>
                        </div>
                      )}

                      {q.question_type === 'scale' && (
                        <div className="form-group">
                          <div className="question-scale-settings-row">
                            <div>
                              <label>Минимальное значение *</label>
                              <div className={`question-type-select-wrap ${scaleMinSelectOpen[index] ? 'open' : ''}`} ref={el => scaleMinSelectRefs.current[index] = el}>
                                <div
                                  className={`question-type-select-custom ${scaleMinSelectOpen[index] ? 'open' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const isCurrentlyOpen = scaleMinSelectOpen[index];
                                    setScaleMinSelectOpen(prev => {
                                      if (isCurrentlyOpen) {
                                        return { ...prev, [index]: false };
                                      }
                                      return { [index]: true };
                                    });
                                    // Закрываем другие открытые списки
                                    setScaleMaxSelectOpen({});
                                  }}
                                  role="combobox"
                                  aria-expanded={scaleMinSelectOpen[index]}
                                  aria-haspopup="listbox"
                                >
                                  <span className="question-type-select-value">
                                    {q.options?.min !== undefined ? q.options.min : 1}
                                  </span>
                                  <span className="question-type-select-arrow">▾</span>
                                </div>
                                {scaleMinSelectOpen[index] && (
                                  <div className="question-type-select-dropdown" role="listbox">
                                    {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                                      <div
                                        key={num}
                                        className={`question-type-select-option ${q.options?.min === num ? 'selected' : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const min = num;
                                          const max = q.options?.max || 5;
                                          handleQuestionChange(index, 'options', {
                                            ...q.options,
                                            min: min,
                                            max: min >= max ? Math.min(min + 1, 10) : max,
                                            label_min: q.options?.label_min || '',
                                            label_max: q.options?.label_max || ''
                                          });
                                          setScaleMinSelectOpen(prev => ({ ...prev, [index]: false }));
                                        }}
                                        role="option"
                                      >
                                        <span className="question-type-select-option-text">{num}</span>
                                        {q.options?.min === num && <span className="question-type-select-option-check">✓</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <label>Подпись для минимального значения</label>
                              <input
                                type="text"
                                value={q.options?.label_min || ''}
                                onChange={(e) => {
                                  handleQuestionChange(index, 'options', {
                                    ...q.options,
                                    min: q.options?.min || 1,
                                    max: q.options?.max || 5,
                                    label_min: e.target.value,
                                    label_max: q.options?.label_max || ''
                                  });
                                }}
                                placeholder="Например: Плохо"
                                style={{ width: '100%', padding: '8px 12px' }}
                              />
                            </div>
                          </div>
                          <div className="question-scale-settings-row">
                            <div>
                              <label>Максимальное значение *</label>
                              <div className={`question-type-select-wrap ${scaleMaxSelectOpen[index] ? 'open' : ''}`} ref={el => scaleMaxSelectRefs.current[index] = el}>
                                <div
                                  className={`question-type-select-custom ${scaleMaxSelectOpen[index] ? 'open' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const isCurrentlyOpen = scaleMaxSelectOpen[index];
                                    setScaleMaxSelectOpen(prev => {
                                      if (isCurrentlyOpen) {
                                        return { ...prev, [index]: false };
                                      }
                                      return { [index]: true };
                                    });
                                    // Закрываем другие открытые списки
                                    setScaleMinSelectOpen({});
                                  }}
                                  role="combobox"
                                  aria-expanded={scaleMaxSelectOpen[index]}
                                  aria-haspopup="listbox"
                                >
                                  <span className="question-type-select-value">
                                    {q.options?.max !== undefined ? q.options.max : 5}
                                  </span>
                                  <span className="question-type-select-arrow">▾</span>
                                </div>
                                {scaleMaxSelectOpen[index] && (
                                  <div className="question-type-select-dropdown" role="listbox">
                                    {Array.from({ length: 10 }, (_, i) => i + 1)
                                      .filter(num => num > (q.options?.min || 1))
                                      .map((num) => (
                                        <div
                                          key={num}
                                          className={`question-type-select-option ${q.options?.max === num ? 'selected' : ''}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const max = num;
                                            const min = q.options?.min || 1;
                                            handleQuestionChange(index, 'options', {
                                              ...q.options,
                                              min: min,
                                              max: max <= min ? Math.min(min + 1, 10) : max,
                                              label_min: q.options?.label_min || '',
                                              label_max: q.options?.label_max || ''
                                            });
                                            setScaleMaxSelectOpen(prev => ({ ...prev, [index]: false }));
                                          }}
                                          role="option"
                                        >
                                          <span className="question-type-select-option-text">{num}</span>
                                          {q.options?.max === num && <span className="question-type-select-option-check">✓</span>}
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <label>Подпись для максимального значения</label>
                              <input
                                type="text"
                                value={q.options?.label_max || ''}
                                onChange={(e) => {
                                  handleQuestionChange(index, 'options', {
                                    ...q.options,
                                    min: q.options?.min || 1,
                                    max: q.options?.max || 5,
                                    label_min: q.options?.label_min || '',
                                    label_max: e.target.value
                                  });
                                }}
                                placeholder="Например: Отлично"
                                style={{ width: '100%', padding: '8px 12px' }}
                              />
                            </div>
                          </div>
                          <small style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            Пользователь сможет выбрать значение от {q.options?.min || 1} до {q.options?.max || 5} на шкале
                          </small>
                        </div>
                      )}

                      <div className="form-group">
                        <div className="question-toggles-divider" aria-hidden="true"></div>
                        <div className="question-toggles-row">
                          <label className="toggle-label">
                            <input
                              type="checkbox"
                              checked={q.is_required}
                              onChange={(e) => handleQuestionChange(index, 'is_required', e.target.checked)}
                              className="toggle-input"
                            />
                            <span className="toggle-switch"></span>
                            <span className="toggle-text">{q.is_required ? 'Обязательный вопрос' : 'Необязательный вопрос'}</span>
                          </label>
                          <label className="toggle-label">
                            <input
                              type="checkbox"
                              checked={expandedDescriptions[index] || false}
                              onChange={(e) => setExpandedDescriptions(prev => ({
                                ...prev,
                                [index]: e.target.checked
                              }))}
                              className="toggle-input"
                            />
                            <span className="toggle-switch"></span>
                            <span className="toggle-text">Добавить описание к вопросу</span>
                          </label>
                        </div>
                        {expandedDescriptions[index] && (
                          <>
                            <textarea
                              value={q.question_description || ''}
                              onChange={(e) => handleQuestionChange(index, 'question_description', e.target.value)}
                              rows="3"
                              placeholder="Дополнительное описание или подсказка к вопросу"
                              style={{ marginTop: '8px' }}
                            />
                            <small style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                              Для форматирования используйте: <strong>**жирный текст**</strong> для жирного шрифта и <strong>__подчеркнутый текст__</strong> для подчеркивания. Описание будет отображаться жирным шрифтом.
                            </small>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />
      {importNotification && (
        <Notification
          message={importNotification.message}
          type={importNotification.type}
          onClose={() => setImportNotification(null)}
          duration={5000}
        />
      )}
      {renameModal && (
        <div className="form-modal-overlay form-modal-overlay--centered import-rename-overlay" onClick={(e) => e.target === e.currentTarget && handleRenameModalSkip()}>
          <div className="form-modal import-rename-modal" onClick={e => e.stopPropagation()}>
            <div className="form-modal-header">
              <h3>Пресет с таким названием уже существует</h3>
            </div>
            <div className="form-modal-body">
              <p className="import-rename-hint">Введите другое название для пресета «{renameModal.defaultName}» или нажмите «Пропустить».</p>
              <input
                type="text"
                className="form-control"
                value={renameInputValue}
                onChange={e => setRenameInputValue(e.target.value)}
                placeholder="Название пресета"
                autoFocus
              />
              <div className="import-rename-actions">
                <button type="button" className="btn btn-primary" onClick={handleRenameModalConfirm}>
                  Импортировать с этим названием
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleRenameModalSkip}>
                  Пропустить
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="form-modal-overlay form-modal-overlay--centered applications-panel-overlay">
        <div className="form-modal applications-panel-modal" onClick={(e) => e.stopPropagation()}>
          <div className="form-modal-header">
            <h3>Управление пресетами вопросов</h3>
            <button type="button" className="btn-close-form" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        <div className="form-modal-body">
          <div className="presets-actions-row">
            <button type="button" className="btn btn-primary" onClick={handleAddClick}>
              Создать пресет
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              {importing ? 'Импорт…' : 'Импорт пресета'}
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept=".json,application/json"
              style={{ display: 'none' }}
              onChange={handleImportPresets}
            />
          </div>
          {localPresets.length === 0 ? (
            <div className="empty-state">Нет пресетов</div>
          ) : (
            <div className="presets-list">
              {localPresets.map(preset => (
                <div key={preset.id} className="preset-card">
                  <div className="preset-card-header">
                    <h4 className="preset-card-title">{preset.name}</h4>
                    <button
                      type="button"
                      className="preset-card-export-btn"
                      onClick={() => handleExportOnePreset(preset.id)}
                      disabled={exportingId === preset.id}
                      title={exportingId === preset.id ? 'Экспорт…' : 'Экспорт пресета'}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                    </button>
                  </div>
                  <div className="preset-card-main">
                    <p className="preset-card-description">
                      Описание: {preset.description && preset.description.trim() ? preset.description.trim() : 'отсутствует'}
                    </p>
                    <p className="preset-card-meta">{preset.questions_count || 0} вопросов</p>
                    {preset.created_at && (
                      <p className="preset-card-date">
                        Создан: {new Date(preset.created_at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Moscow' })}
                      </p>
                    )}
                  </div>
                  <div className="preset-card-actions">
                    <button
                      type="button"
                      className="btn-action btn-secondary"
                      onClick={() => handleEditClick(preset.id)}
                    >
                      Редактировать
                    </button>
                    <button
                      type="button"
                      className="btn-action btn-danger"
                      onClick={() => handleDelete(preset.id)}
                    >
                      Удалить
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>
      </>
  );
};

/** ISO (UTC) → календарная дата + время в МСК для пикеров */
function autoCloseFromIso(iso) {
  if (!iso) return { date: null, time: '' };
  let str = String(iso).trim();
  if (!/Z|[+-]\d{2}:?\d{2}$/.test(str)) {
    str = str.replace(' ', 'T') + 'Z';
  }
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return { date: null, time: '' };

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);

  const get = (type) => parts.find((p) => p.type === type)?.value;
  const year = parseInt(get('year'), 10);
  const month = parseInt(get('month'), 10);
  const day = parseInt(get('day'), 10);
  let hour = parseInt(get('hour'), 10);
  let minute = parseInt(get('minute'), 10);
  if (hour === 24) hour = 0;
  minute = Math.round(minute / 5) * 5;
  if (minute === 60) minute = 55;

  return {
    date: new Date(year, month - 1, day),
    time: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
  };
}

/** Локальная календарная дата + HH:MM (как МСК) → ISO UTC */
function autoCloseToIso(date, time) {
  if (!date || !time) return '';
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(time).trim());
  if (!m) return '';
  const hours = Math.min(23, Math.max(0, parseInt(m[1], 10)));
  let minutes = Math.min(59, Math.max(0, parseInt(m[2], 10)));
  minutes = Math.round(minutes / 5) * 5;
  if (minutes === 60) minutes = 55;
  const y = date.getFullYear();
  const mo = date.getMonth() + 1;
  const day = date.getDate();
  const msk = `${y}-${String(mo).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00+03:00`;
  const parsed = new Date(msk);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString();
}

function getMskTodayDate() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return new Date(parseInt(get('year'), 10), parseInt(get('month'), 10) - 1, parseInt(get('day'), 10));
}

// Компонент модального окна настроек
const SettingsModal = ({
  settingsData,
  siteRoles,
  isCuratorModerator,
  isSubmitting,
  onSettingsChange,
  onAllowedRoleChange,
  onSave,
  onClose
}) => {
  const [initialSettingsData, setInitialSettingsData] = React.useState(null);
  const [showUnsavedModal, setShowUnsavedModal] = React.useState(false);
  const autoCloseParts = React.useMemo(
    () => autoCloseFromIso(settingsData.auto_close_at),
    [settingsData.auto_close_at],
  );

  // Сохраняем начальное состояние при открытии
  React.useEffect(() => {
    setInitialSettingsData(JSON.parse(JSON.stringify(settingsData)));
  }, []);

  // Функция проверки изменений
  const hasUnsavedChanges = () => {
    if (!initialSettingsData) return false;
    return JSON.stringify(settingsData) !== JSON.stringify(initialSettingsData);
  };

  // Обработчик закрытия с проверкой изменений
  const handleCloseClick = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedModal(true);
    } else {
      onClose();
    }
  };

  // Сохранить и закрыть
  const handleSaveAndClose = async () => {
    setShowUnsavedModal(false);
    await onSave();
    onClose();
  };

  // Закрыть без сохранения
  const handleCloseWithoutSave = () => {
    setShowUnsavedModal(false);
    onClose();
  };

  return (
    <div className="form-modal-overlay form-modal-overlay--centered applications-settings-overlay" onClick={handleCloseClick}>
      <div className="form-modal applications-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="form-modal-header">
          <div className="header-content">
            <h3>Настройки формы</h3>
          </div>
          <div className="header-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSave}
              disabled={isSubmitting}
              style={{ marginRight: '8px' }}
            >
              {isSubmitting ? 'Сохранение...' : 'Сохранить'}
            </button>
            <button type="button" className="btn-close-form" onClick={handleCloseClick} title="Закрыть">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
        <UnsavedChangesModal
          isOpen={showUnsavedModal}
          onClose={() => setShowUnsavedModal(false)}
          onSaveAndClose={handleSaveAndClose}
          onCloseWithoutSave={handleCloseWithoutSave}
        />
        <div className="form-modal-body">
          <div className="form-group">
            <div className="toggle-group">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  className="toggle-input"
                  checked={settingsData.auto_close_enabled || false}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    onSettingsChange('auto_close_enabled', enabled);
                    if (enabled && !settingsData.auto_close_at) {
                      onSettingsChange('auto_close_at', autoCloseToIso(getMskTodayDate(), '12:00'));
                    }
                  }}
                />
                <span className="toggle-switch"></span>
                <span className="toggle-text">Автоматическое закрытие</span>
              </label>
            </div>
            <small style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px', display: 'block', marginBottom: settingsData.auto_close_enabled ? '12px' : '0' }}>
              {settingsData.auto_close_enabled
                ? 'Форма закроется в указанное ниже время (МСК). Можно выключить, чтобы отменить автозакрытие.'
                : 'Включите, чтобы форма автоматически закрылась в заданное время'}
            </small>
            {settingsData.auto_close_enabled && (
              <div className="auto-close-pickers">
                <div className="auto-close-pickers__row">
                  <div className="auto-close-pickers__field">
                    <span className="auto-close-pickers__label">Дата (МСК)</span>
                    <div className="auto-close-pickers__control">
                      <DatePicker
                        selectedDate={autoCloseParts.date || getMskTodayDate()}
                        minDate={getMskTodayDate()}
                        onDateChange={(date) => {
                          const time = autoCloseParts.time || '12:00';
                          onSettingsChange('auto_close_at', autoCloseToIso(date, time));
                        }}
                      />
                    </div>
                  </div>
                  <div className="auto-close-pickers__field">
                    <span className="auto-close-pickers__label">Время (МСК)</span>
                    <div className="auto-close-pickers__control">
                      <TimePicker
                        value={autoCloseParts.time || '12:00'}
                        onChange={(time) => {
                          const date = autoCloseParts.date || getMskTodayDate();
                          onSettingsChange('auto_close_at', autoCloseToIso(date, time));
                        }}
                      />
                    </div>
                  </div>
                </div>
                <small className="auto-close-pickers__hint">
                  Форма закроется в выбранные дату и время по Москве. Часы — каждый, минуты — шаг 5.
                </small>
              </div>
            )}
          </div>

          {isCuratorModerator && (
            <div className="form-group">
              <div className="toggle-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    className="toggle-input"
                    checked={settingsData.restrict_viewers || false}
                    onChange={(e) => onSettingsChange('restrict_viewers', e.target.checked)}
                  />
                  <span className="toggle-switch"></span>
                  <span className="toggle-text">Ограничить доступ к просмотру заявок</span>
                </label>
              </div>
              <small style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px', display: 'block', marginBottom: '12px' }}>
                {settingsData.restrict_viewers 
                  ? 'Только admin и пользователи с выбранными ролями смогут просматривать отправленные заявки'
                  : 'Все авторизованные пользователи смогут просматривать отправленные заявки'}
              </small>

              {settingsData.restrict_viewers && (
                <>
                  <div className="viewers-info" style={{ marginBottom: '12px', padding: '10px', background: 'var(--bg-secondary)', borderRadius: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    <strong>admin</strong> всегда имеет доступ к просмотру заявок
                  </div>
                  <label htmlFor="allowed_roles" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                    Выберите роли с доступом к просмотру:
                  </label>
                  <div className="viewers-select-container">
                    {siteRoles.length > 0 ? (
                      siteRoles.map(roleName => (
                        <label key={roleName} className="viewer-checkbox-label">
                          <input
                            type="checkbox"
                            checked={settingsData.allowed_roles?.includes(roleName) || false}
                            onChange={(e) => onAllowedRoleChange(roleName, e.target.checked)}
                          />
                          <span>{roleName}</span>
                        </label>
                      ))
                    ) : (
                      <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '14px' }}>
                        Загрузка списка ролей...
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Applications;

